<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="1280px"
    top="3vh"
    :close-on-click-modal="false"
    custom-class="sk-dialog sk-dialog--xl tdi-excel-wizard-dialog"
    :before-close="handleBeforeClose"
    @closed="resetState"
  >
    <div slot="title" class="wizard-dialog-title">
      <div class="wizard-title-main">
        <i class="el-icon-upload" style="color: #2563eb; margin-right: 8px;"></i>
        <span>Khởi Tạo Phiếu Thu Học Sinh Từ File Excel (Lớp / Nhóm)</span>
      </div>
      <span class="wizard-title-sub">
        Hướng dẫn từng bước tải file mẫu (2 sheets kèm danh mục khoản thu), cấu hình đợt thu, đối soát dữ liệu.
      </span>
    </div>

    <!-- Thanh tiến trình Wizard 3 bước -->
    <div class="wizard-steps-wrapper">
      <el-steps :active="activeStep" finish-status="success" align-center>
        <el-step title="Bước 1" description="Chọn học sinh & Tải file mẫu" icon="el-icon-download" />
        <el-step title="Bước 2" description="Cấu hình & Tải file lên đối soát" icon="el-icon-upload2" />
        <el-step title="Bước 3" description="Xem trước & Khởi tạo dữ liệu DB" icon="el-icon-view" />
      </el-steps>
    </div>

    <!-- ========================================================================= -->
    <!-- BƯỚC 1: CHỌN HỌC SINH & TẢI FILE MẪU EXCEL -->
    <!-- ========================================================================= -->
    <div v-show="activeStep === 0" class="sk-dialog__content">
      <!-- Cấu hình thông tin đợt thu & Danh sách học sinh từ bộ lọc ngoài -->
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon">
            <i class="el-icon-date"></i>
          </div>
          <div>
            <p class="sk-panel-title">1. Thông tin đợt thu & Danh sách học sinh</p>
            <p class="sk-panel-subtitle">Dữ liệu học sinh và các khoản thu đã khởi tạo được lấy trực tiếp từ màn hình chính</p>
          </div>
        </div>

        <div class="wizard-filter-grid">
          <!-- Phạm vi (Lớp / Nhóm) -->
          <div class="filter-item">
            <span class="filter-label">Phạm vi:</span>
            <el-tag size="small" type="primary" effect="dark" style="font-weight: 600;">
              <i :class="step1Filter.targetType === 'GROUP' ? 'el-icon-s-custom' : 'el-icon-school'"></i>
              {{ scopeName || currentClassName || (step1Filter.targetType === 'GROUP' ? 'Theo nhóm' : 'Theo lớp') }}
            </el-tag>
          </div>

          <!-- Disable chọn tháng -->
          <div class="filter-item">
            <span class="filter-label">Tháng:</span>
            <el-date-picker
              v-model="step1Filter.monthAndYear"
              type="month"
              format="MM-yyyy"
              value-format="M-yyyy"
              placeholder="Chọn tháng"
              size="small"
              style="width: 130px"
              :clearable="false"
              disabled
            />
          </div>

          <!-- Disable chọn đợt (nếu ở màn ngoài không có đợt thì là đợt 1, có đợt thì ghi nhận) -->
          <div class="filter-item">
            <span class="filter-label">Đợt thu:</span>
            <el-select
              v-model="step1Filter.turn"
              placeholder="Chọn đợt"
              size="small"
              style="width: 110px"
              disabled
            >
              <el-option
                v-for="t in 20"
                :key="t"
                :value="t"
                :label="`Đợt ${t}`"
              />
            </el-select>
          </div>
        </div>

        <!-- Tùy chọn điểm danh và thiếu/thừa trước -->
        <div class="wizard-options-row">
          <div class="option-check-item">
            <el-switch v-model="step1Options.includeAttendance" active-color="#10b981" />
            <span class="option-check-label">
              <strong>Lấy dữ liệu điểm danh</strong>
            </span>
            <!-- Người dùng chọn tháng điểm danh độc lập với kỳ thu, kể cả tháng tương lai. -->
            <el-date-picker
              v-if="step1Options.includeAttendance"
              v-model="step1Options.attendanceMonth"
              type="month"
              format="MM-yyyy"
              value-format="M-yyyy"
              placeholder="Chọn tháng điểm danh"
              size="mini"
              :clearable="false"
              :picker-options="attendancePickerOptions"
              style="width: 140px; margin-left: 6px;"
            />
          </div>

          <div class="option-check-item">
            <el-switch v-model="step1Options.includePreviousDebt" active-color="#10b981" />
            <span class="option-check-label">
              <strong>Lấy thiếu/thừa của phiếu thu trước</strong>
            </span>
          </div>
        </div>

        <!-- Bảng danh sách học sinh: KHÔNG PHÂN TRANG -->
        <div class="wizard-sub-section" style="margin-top: 12px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="font-weight: 600; font-size: 13px; color: #334155;">
                Danh sách học sinh (Đã chọn: <strong style="color: #2563eb;">{{ selectedStudents.length }}</strong> / {{ studentList.length }})
              </div>
              <el-button size="mini" type="text" style="color: #2563eb; font-weight: 600; padding: 0 4px;" @click="selectAllStudents">
                <i class="el-icon-check"></i> Chọn tất cả
              </el-button>
              <span style="color: #cbd5e1;">|</span>
              <el-button size="mini" type="text" style="color: #64748b; padding: 0 4px;" @click="clearStudentSelection">
                <i class="el-icon-close"></i> Bỏ chọn
              </el-button>
            </div>
            <div>
              <el-input
                v-model="studentKeyword"
                placeholder="Tìm mã hoặc tên học sinh..."
                size="mini"
                prefix-icon="el-icon-search"
                clearable
                style="width: 250px;"
              />
            </div>
          </div>

          <el-table
            ref="studentTable"
            :data="filteredStudents"
            :row-key="getStudentRowKey"
            border
            size="mini"
            height="260"
            class="sk-table"
            v-loading="studentLoading"
            @selection-change="handleStudentSelectionChange"
          >
            <el-table-column type="selection" :reserve-selection="true" width="45" align="center" />
            <el-table-column label="STT" type="index" width="50" align="center" />
            <el-table-column prop="kidCode" label="Mã học sinh" width="120" align="center">
              <template slot-scope="{ row }">
                <span style="font-weight: 600; color: #1e293b;">{{ row.studentCode || row.kidCode }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="fullName" label="Họ và tên" min-width="160">
              <template slot-scope="{ row }">
                <span>{{ row.fullName || row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="birthday" label="Ngày sinh" width="110" align="center">
              <template slot-scope="{ row }">
                <span>{{ row.birthday ? formatDate(row.birthday) : '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="nickName" label="Biệt danh" width="110" align="center">
              <template slot-scope="{ row }">{{ row.nickName || '—' }}</template>
            </el-table-column>
            <el-table-column prop="className" label="Lớp" width="130" align="center">
              <template slot-scope="{ row }">{{ row.className || scopeName || currentClassName }}</template>
            </el-table-column>
            <el-table-column label="Số khoản đã tạo" width="120" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" type="success" effect="plain">
                  {{ (row.invoiceDetails && row.invoiceDetails.length) || 0 }} khoản
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- Cấu hình các khoản thu trong file mẫu (Sheet 2) -->
      <div class="sk-panel" style="margin-top: 14px;">
        <div class="sk-panel-head" style="display: flex; justify-content: space-between; align-items: center;">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div class="sk-panel-icon">
              <i class="el-icon-tickets"></i>
            </div>
            <div>
              <p class="sk-panel-title">2. Danh mục các khoản thu</p>
              <p class="sk-panel-subtitle">Bao gồm {{ configuredFeeColumns.length }} khoản thu. Các khoản kế thừa từ màn chính không được xóa.</p>
            </div>
          </div>
          <div style="display: flex; gap: 10px; align-items: center;">
            <el-button
              size="small"
              class="btn-redesign-add-fee"
              icon="el-icon-circle-plus"
              @click="openAddCustomFeeDialog"
            >
              Thêm khoản thu mới
            </el-button>
          </div>
        </div>

        <!-- Khu vực nạp file mẫu hỗ trợ kéo thả file (Thiết kế mới) -->
        <input
          ref="templateCatalogFileInput"
          type="file"
          accept=".xlsx"
          style="display: none;"
          @change="handleTemplateCatalogUpload"
        />

        <!-- Trạng thái 1: Chưa nạp file -> Khung kéo thả trực quan, hiện đại -->
        <div
          v-if="!uploadedTemplateCatalogFile"
          class="old-template-dropzone"
          :class="{ 'is-dragover': isTemplateCatalogDragOver }"
          @dragover.prevent="isTemplateCatalogDragOver = true"
          @dragleave.prevent="isTemplateCatalogDragOver = false"
          @drop.prevent="handleTemplateCatalogDrop"
          @click="$refs.templateCatalogFileInput.click()"
        >
          <div class="old-template-dropzone-inner">
            <div class="old-template-dropzone-left">
              <div class="old-template-dropzone-icon">
                <i class="el-icon-upload"></i>
              </div>
              <div class="old-template-dropzone-text">
                <div class="main-text">
                  <strong>Kéo thả file mẫu vào đây</strong> hoặc <span class="highlight-browse">bấm để nạp file</span>
                </div>
                <div class="sub-text">
                  Lấy danh mục từ Sheet <code>Khoản Thu</code> trong file Excel (<code>.xlsx</code>)
                </div>
              </div>
            </div>
            <el-button
              size="small"
              class="btn-browse-old-template"
              icon="el-icon-folder-opened"
              @click.stop="$refs.templateCatalogFileInput.click()"
            >
              Chọn file mẫu
            </el-button>
          </div>
        </div>

        <!-- Trạng thái 2: Đã nạp file -> Card thông tin chi tiết (Vẫn hỗ trợ kéo thả file khác để thay thế) -->
        <div
          v-else
          class="old-template-active-card"
          :class="{ 'is-dragover': isTemplateCatalogDragOver }"
          @dragover.prevent="isTemplateCatalogDragOver = true"
          @dragleave.prevent="isTemplateCatalogDragOver = false"
          @drop.prevent="handleTemplateCatalogDrop"
        >
          <div class="old-template-card-top">
            <div class="old-template-file-info">
              <div class="old-template-file-badge">
                <i class="el-icon-document-checked"></i>
              </div>
              <div class="old-template-file-details">
                <div class="file-name" :title="uploadedTemplateCatalogFile.name">
                  <i class="el-icon-document" style="color: #10b981; margin-right: 4px;"></i>
                  {{ uploadedTemplateCatalogFile.name }}
                </div>
                <div class="file-meta">
                  <span class="file-size">{{ (uploadedTemplateCatalogFile.size / 1024).toFixed(1) }} KB</span>
                  <span class="file-divider">•</span>
                  <span class="file-fee-count">
                    Đã trích xuất <strong style="color: #059669;">{{ importedTemplateFees.length }}</strong> khoản thu
                  </span>
                  <span class="file-divider">•</span>
                  <span class="file-drag-hint">(Kéo thả file khác vào đây để thay thế)</span>
                </div>
              </div>
            </div>

            <div class="old-template-actions">
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-refresh"
                class="btn-change-old-file"
                @click.stop="$refs.templateCatalogFileInput.click()"
              >
                Đổi file khác
              </el-button>
              <el-button
                size="mini"
                type="danger"
                plain
                icon="el-icon-delete"
                class="btn-remove-old-file"
                @click.stop="removeTemplateCatalogFile"
              >
                Gỡ file
              </el-button>
            </div>
          </div>

          <div class="old-template-card-bottom">
            <span class="switch-title">Nguồn khoản thu đưa vào file mẫu:</span>
            <div class="switch-options">
              <el-checkbox v-model="useSystemFees" @change="recomputeConfiguredFees">
                Khoản từ hệ thống ({{ systemFeeColumns.length }})
              </el-checkbox>
              <el-checkbox v-model="useTemplateCatalogFees" @change="recomputeConfiguredFees">
                Khoản từ file mẫu ({{ importedTemplateFees.length }})
              </el-checkbox>
            </div>
          </div>
        </div>

        <!-- Nút tải file mẫu Excel đặt ngay dưới khu vực kéo thả file mẫu -->
        <div class="template-download-wrapper">
          <el-button
            size="small"
            class="btn-redesign-download-template"
            icon="el-icon-download"
            :loading="downloadingTemplate"
            @click="handleDownloadTemplate()"
          >
            Tải file mẫu Excel
          </el-button>
          <span class="template-download-hint">
            <i class="el-icon-info" style="color: #3b82f6;"></i>
            File mẫu gồm 2 Sheets, mỗi khoản có 5 cột dữ liệu. Mã và tên lấy từ Sheet Khoản Thu.
          </span>
        </div>

        <div class="sk-note sk-note--warning" style="margin-bottom: 10px;">
          <i class="el-icon-info"></i>
          <span>Ghi chú: Toàn bộ thông tin các khoản mà học sinh đã được khởi tạo từ trước (Số lượng, Đơn giá, Giảm giá, Thành tiền) sẽ được tự động điền vào file Excel. File Excel khi tải lên ở Bước 2 bắt buộc phải có tối thiểu đầy đủ các khoản thu này.</span>
        </div>

        <!-- Danh mục khoản thu: Thiết kế dạng CARD lớn, trực quan, chuyên nghiệp -->
        <div class="fee-cards-container">
          <div class="fee-cards-grid">
            <div
              v-for="(fee, idx) in configuredFeeColumns"
              :key="idx"
              :class="['fee-card-box', fee.isCustom ? 'fee-card-box--custom' : 'fee-card-box--system']"
            >
              <!-- Card Top Header -->
              <div class="fee-card-top">
                <div class="fee-card-main-meta">
                  <div :class="['fee-card-avatar', fee.isCustom ? 'fee-card-avatar--custom' : 'fee-card-avatar--system']">
                    <i :class="fee.isCustom ? 'el-icon-edit-outline' : 'el-icon-coin'"></i>
                  </div>
                  <div class="fee-card-heading">
                    <span class="fee-card-index">Khoản {{ idx + 1 }}</span>
                    <h4 class="fee-card-title" :title="fee.name || fee.revenueName">
                      {{ fee.name || fee.revenueName }}
                    </h4>
                  </div>
                </div>

                <div class="fee-card-badge-wrap">
                  <el-tag
                    v-if="!fee.isCustom"
                    size="small"
                    :type="fee.source === 'Cấu hình lớp' ? 'primary' : (fee.source === 'Phiếu thu đã phát hành' ? 'success' : 'info')"
                    effect="dark"
                    class="fee-source-pill"
                  >
                    <i :class="fee.source === 'Phiếu thu đã phát hành' ? 'el-icon-document-checked' : 'el-icon-lock'"></i>
                    {{ fee.source || 'Hệ thống' }}
                  </el-tag>
                  <div v-else class="fee-custom-actions">
                    <el-tag
                      size="small"
                      :type="fee.source === 'Từ file mẫu' ? 'warning' : 'success'"
                      effect="dark"
                      class="fee-source-pill"
                    >
                      <i :class="fee.source === 'Từ file mẫu' ? 'el-icon-document' : 'el-icon-circle-plus-outline'"></i>
                      {{ fee.source || 'Tự thêm' }}
                    </el-tag>
                    <el-button
                      type="danger"
                      size="mini"
                      icon="el-icon-delete"
                      circle
                      class="fee-delete-btn"
                      :title="fee.source === 'Từ file mẫu' ? 'Xóa khoản thu từ file mẫu này' : 'Xóa khoản thu tự thêm này'"
                      @click.stop="removeConfiguredFee(idx)"
                    />
                  </div>
                </div>
              </div>

              <!-- Card Key Metrics / Info Row -->
              <div class="fee-card-info-grid">
                <!-- Mã khoản thu -->
                <div class="fee-info-block fee-info-block--code">
                  <span class="info-block-label">MÃ KHOẢN (EXCEL)</span>
                  <div class="info-block-value-code" :title="fee.revenueCode || 'Chưa gán mã'">
                    <i class="el-icon-document" style="margin-right: 4px; color: #3b82f6;"></i>
                    {{ fee.revenueCode || '—' }}
                  </div>
                </div>

                <!-- Đơn vị tính -->
                <div class="fee-info-block fee-info-block--unit">
                  <span class="info-block-label">ĐƠN VỊ TÍNH</span>
                  <div class="info-block-value-unit">
                    {{ fee.unit || 'Tháng' }}
                  </div>
                </div>
              </div>

              <!-- Card Price Display (To rõ ràng) -->
              <div class="fee-card-price-hero">
                <span class="price-hero-label">Đơn giá định mức:</span>
                <div class="price-hero-figure">
                  <span class="price-number">{{ formatMoney(fee.price) }}</span>
                  <span class="price-currency">VNĐ</span>
                </div>
              </div>

              <!-- Card Footer Note -->
              <div class="fee-card-bottom">
                <span class="bottom-tag">
                  <i class="el-icon-circle-check" style="color: #10b981; margin-right: 4px;"></i>
                  {{ fee.isCustom ? (fee.source === 'Từ file mẫu' ? 'Khoản nạp từ file mẫu' : 'Khoản tự thêm trong file mẫu') : 'Được bảo lưu từ cấu hình học phí' }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="configuredFeeColumns.length === 0" class="fee-cards-empty">
            <i class="el-icon-warning-outline"></i>
            <p>Chưa có khoản thu nào trong danh mục. Bấm nút <strong>"Thêm khoản thu mới"</strong> ở trên hoặc kiểm tra lại màn hình chính.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- BƯỚC 2: CẤU HÌNH ĐỢT THU & TẢI FILE EXCEL LÊN ĐỐI SOÁT -->
    <!-- ========================================================================= -->
    <div v-show="activeStep === 1" class="sk-dialog__content">
      <!-- Bố cục 2 cột song song hiện đại, đối xứng -->
      <div class="step2-layout-grid">
        <!-- Cột 1: Thông tin đợt thu -->
        <div class="sk-panel step2-card">
          <div class="sk-panel-head">
            <div class="sk-panel-icon">
              <i class="el-icon-date"></i>
            </div>
            <div>
              <p class="sk-panel-title">1. Thông tin đợt thu</p>
              <p class="sk-panel-subtitle">Thời gian hiệu lực và mô tả của đợt thu</p>
            </div>
          </div>

          <div class="step2-card-body">
            <!-- Thẻ thông tin ngữ cảnh kế thừa -->
            <div class="period-info-tags">
              <span class="info-tag-label">Áp dụng:</span>
              <el-tag size="mini" type="primary" effect="plain">
                <i :class="step1Filter.targetType === 'GROUP' ? 'el-icon-s-custom' : 'el-icon-school'"></i>
                {{ scopeName || currentClassName || 'Theo danh sách' }}
              </el-tag>
              <el-tag size="mini" type="info" effect="plain">
                Đợt {{ invoicePeriod.turn || 1 }}
              </el-tag>
              <el-tag size="mini" type="warning" effect="plain">
                Tháng {{ invoicePeriod.monthAndYear || step1Filter.monthAndYear }}
              </el-tag>
            </div>

            <div class="form-control-item" style="margin-bottom: 12px;">
              <div style="display: flex; gap: 12px;">
                <div style="flex: 1;">
                  <label class="control-label">Tháng thu:</label>
                  <el-input
                    :value="invoicePeriod.monthAndYear || step1Filter.monthAndYear"
                    disabled
                    size="small"
                    prefix-icon="el-icon-date"
                  />
                </div>
                <div style="flex: 1;">
                  <label class="control-label">Đợt thu <span style="color: red;">*</span>:</label>
                  <el-select
                    v-model="invoicePeriod.turn"
                    placeholder="Chọn đợt thu"
                    size="small"
                    style="width: 100%;"
                    @change="handleStep2TurnChange"
                  >
                    <el-option
                      v-for="t in 20"
                      :key="t"
                      :value="t"
                      :label="`Đợt ${t}`"
                    />
                  </el-select>
                </div>
              </div>
            </div>

            <div class="form-control-item" style="margin-bottom: 12px;">
              <label class="control-label">Khoảng thời gian hiệu lực <span style="color: red;">*</span>:</label>
              <el-date-picker
                v-model="invoicePeriod.dateRange"
                type="daterange"
                format="dd-MM-yyyy"
                value-format="yyyy-MM-dd"
                range-separator="-"
                start-placeholder="Từ ngày"
                end-placeholder="Đến ngày"
                size="small"
                style="width: 100%;"
                :picker-options="pickerOptions"
              />
            </div>

            <div class="form-control-item">
              <label class="control-label">Mô tả đợt thu <span style="color: red;">*</span>:</label>
              <el-input
                v-model="invoicePeriod.description"
                type="textarea"
                :rows="3"
                placeholder="Ví dụ: Phiếu thu học phí tháng 09/2026"
                clearable
              />
            </div>
          </div>
        </div>

        <!-- Cột 2: Tải file Excel đối soát -->
        <div class="sk-panel step2-card">
          <div class="sk-panel-head">
            <div class="sk-panel-icon sk-panel-icon--success">
              <i class="el-icon-upload2"></i>
            </div>
            <div>
              <p class="sk-panel-title">2. Tải file Excel đối soát</p>
              <p class="sk-panel-subtitle">File Excel .xlsx gồm 2 sheets (Danh Sách HS & Khoản Thu)</p>
            </div>
          </div>

          <div class="step2-card-body">
            <div class="upload-dropzone-container">
              <input
                ref="excelFileInput"
                type="file"
                accept=".xlsx"
                style="display: none;"
                @change="handleFileUpload"
              />
              <div
                class="upload-dropzone"
                :class="{ 'is-dragover': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleFileDrop"
                @click="$refs.excelFileInput.click()"
              >
                <i class="el-icon-upload upload-dropzone-icon"></i>
                <div class="upload-dropzone-text">
                  <strong>Bấm để chọn file Excel</strong> hoặc kéo thả vào đây
                </div>
                <div class="upload-dropzone-hint">
                  File mẫu <code>.xlsx</code> với 2 Sheets (Danh Sách HS, Khoản Thu)
                </div>
              </div>
            </div>

            <!-- Thông tin file đã tải lên -->
            <div v-if="uploadedFile" class="uploaded-file-card">
              <div style="display: flex; align-items: center; gap: 10px; overflow: hidden;">
                <i class="el-icon-document" style="font-size: 22px; color: #10b981; flex-shrink: 0;"></i>
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <div style="font-weight: 600; color: #1e293b; font-size: 13px;" :title="uploadedFile.name">{{ uploadedFile.name }}</div>
                  <div style="font-size: 11px; color: #64748b;">
                    {{ (uploadedFile.size / 1024).toFixed(1) }} KB | {{ parsedRows.length }} dòng
                    <span v-if="hasFeeSheet" style="color: #2563eb; margin-left: 4px;">(Đã nhận diện Sheet "Khoản Thu")</span>
                  </div>
                </div>
              </div>
              <el-button size="mini" type="danger" plain icon="el-icon-delete" @click="clearUploadedFile">
                Gỡ file
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Kết quả kiểm tra đối soát (Validation Summary & Error Breakdown) -->
      <div v-if="hasValidated" class="sk-panel" style="margin-top: 14px;">
        <div class="sk-panel-head">
          <div class="sk-panel-icon" :class="errorList.length > 0 ? 'sk-panel-icon--danger' : 'sk-panel-icon--success'">
            <i :class="errorList.length > 0 ? 'el-icon-warning' : 'el-icon-circle-check'"></i>
          </div>
          <div>
            <p class="sk-panel-title">3. Kết quả đối soát dữ liệu</p>
            <p class="sk-panel-subtitle">Tổng hợp kiểm tra cấu trúc file, mã học sinh, tên học sinh, danh mục khoản thu bắt buộc và mã phiếu thu</p>
          </div>
        </div>

        <!-- Thẻ KPI thống kê -->
        <div class="kpi-cards-grid">
          <div class="kpi-card">
            <div class="kpi-title">Tổng số dòng</div>
            <div class="kpi-value">{{ parsedRows.length }}</div>
          </div>
          <div class="kpi-card" :class="{ 'kpi-card--success': errorList.length === 0 }">
            <div class="kpi-title">Số dòng hợp lệ</div>
            <div class="kpi-value" style="color: #10b981;">{{ validRowCount }}</div>
          </div>
          <div class="kpi-card" :class="{ 'kpi-card--danger': errorList.length > 0 }">
            <div class="kpi-title">Số lỗi phát hiện</div>
            <div class="kpi-value" :style="{ color: errorList.length > 0 ? '#ef4444' : '#10b981' }">
              {{ errorList.length }}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Số khoản thu phát hiện</div>
            <div class="kpi-value" style="color: #6366f1;">{{ detectedFeeColumns.length }}</div>
          </div>
        </div>

        <!-- Trạng thái đang kiểm tra thanh toán online từ BE -->
        <div v-if="checkingOnlinePayment" class="sk-note" style="background: #eff6ff; border-left: 4px solid #3b82f6; color: #1e40af; margin-bottom: 12px; padding: 10px 14px; border-radius: 4px; display: flex; align-items: center; gap: 10px;">
          <i class="el-icon-loading" style="font-size: 20px; color: #2563eb;"></i>
          <div>
            <strong>Đang kiểm tra trạng thái thanh toán trực tuyến từ hệ thống...</strong>
            <div style="font-size: 12px; margin-top: 2px;">FE đối soát hợp lệ 100%. Đang kết nối Backend để kiểm tra xem có phiếu thu nào đã thanh toán online hay không.</div>
          </div>
        </div>

        <!-- Cảnh báo lỗi theo guidelines -->
        <div v-else-if="errorList.length > 0" class="sk-note sk-note--danger">
          <i class="el-icon-warning"></i>
          <div>
            <strong>File Excel phát hiện {{ errorList.length }} lỗi!</strong>
            <div>Theo quy định, bạn phải sửa toàn bộ các lỗi dưới đây trên file Excel và tải lên lại cho đến khi hết lỗi mới được phép tiếp tục.</div>
          </div>
        </div>

        <div v-else class="sk-note sk-note--success">
          <i class="el-icon-circle-check"></i>
          <div>
            <strong>Dữ liệu file Excel hoàn toàn hợp lệ (100%)!</strong>
            <div>Đã vượt qua đối soát và kiểm tra trạng thái thanh toán trực tuyến thành công. Bạn có thể bấm nút "Tiếp tục sang Bước 3".</div>
          </div>
        </div>

        <!-- Bảng chi tiết lỗi (Error Table) -->
        <div v-if="errorList.length > 0" style="margin-top: 10px;">
          <el-table :data="errorList" border size="mini" max-height="280" class="sk-table">
            <el-table-column prop="row" label="Vị trí / Dòng" width="120" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="typeof row.row === 'number' ? 'danger' : 'warning'">
                  {{ typeof row.row === 'number' ? `Dòng ${row.row}` : row.row }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="studentCode" label="Mã học sinh" width="120" align="center">
              <template slot-scope="{ row }">
                <span style="font-weight: 600;">{{ row.studentCode || '—' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="studentName" label="Họ và tên" width="150">
              <template slot-scope="{ row }">{{ row.studentName || '—' }}</template>
            </el-table-column>
            <el-table-column prop="field" label="Tiêu chí đối soát" width="160">
              <template slot-scope="{ row }">
                <span style="font-weight: 600; color: #b91c1c;">{{ row.field }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="value" label="Giá trị trên file" width="140" align="center">
              <template slot-scope="{ row }">
                <code style="color: #b91c1c;">{{ row.value !== undefined && row.value !== null && row.value !== '' ? row.value : '(Trống)' }}</code>
              </template>
            </el-table-column>
            <el-table-column prop="expected" label="Giá trị chuẩn hệ thống" width="170" align="center">
              <template slot-scope="{ row }">
                <span v-if="row.expected" style="color: #15803d; font-weight: 600;">{{ row.expected }}</span>
                <span v-else style="color: #94a3b8;">—</span>
              </template>
            </el-table-column>
            <el-table-column prop="message" label="Mô tả lỗi & Hướng dẫn sửa" min-width="260">
              <template slot-scope="{ row }">
                <span style="color: #dc2626;">{{ row.message }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- BƯỚC 3: XEM TRƯỚC & TẠO DỮ LIỆU CHUẨN DB TRÊN FRONTEND -->
    <!-- ========================================================================= -->
    <div v-if="activeStep === 2" class="sk-dialog__content">
      <div class="sk-panel">
        <div class="sk-panel-head" style="margin-bottom: 12px;">
          <div class="sk-panel-icon">
            <i class="el-icon-coin"></i>
          </div>
          <div>
            <p class="sk-panel-title">Dữ liệu xem trước</p>
            <p class="sk-panel-subtitle">
              Danh sách các phiếu thu sẽ được khởi tạo vào hệ thống cho <strong>Tháng {{ invoicePeriod.monthAndYear || step1Filter.monthAndYear }} - Đợt {{ invoicePeriod.turn || 1 }}</strong> (gồm chi tiết từng khoản thu)
            </p>
          </div>
        </div>

        <div class="kpi-cards-grid" style="margin-bottom: 12px; grid-template-columns: repeat(5, 1fr);">
          <div class="kpi-card">
            <div class="kpi-title">Tổng số phiếu thu</div>
            <div class="kpi-value" style="color: #2563eb;">{{ generatedDbInvoices.length }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Tổng thiếu/thừa trước</div>
            <div class="kpi-value" :style="{ color: totalBeforeMoney < 0 ? '#dc2626' : (totalBeforeMoney > 0 ? '#16a34a' : '#64748b') }">
              {{ formatMoney(totalBeforeMoney) }} đ
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Tổng tiền phải thu</div>
            <div class="kpi-value" style="color: #ea580c;">{{ formatMoney(totalSumMoney) }} đ</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Tổng tiền đã thu</div>
            <div class="kpi-value" style="color: #16a34a;">{{ formatMoney(totalCollectedMoney) }} đ</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">Tổng còn lại</div>
            <div class="kpi-value" :style="{ color: totalAfterMoney < 0 ? '#dc2626' : '#16a34a' }">{{ formatMoney(totalAfterMoney) }} đ</div>
          </div>
        </div>

        <el-table
          ref="previewTable"
          :key="'preview_tbl_' + generatedDbInvoices.length"
          :data="generatedDbInvoices"
          border
          size="mini"
          height="350"
          class="sk-table"
        >
          <el-table-column label="STT" type="index" width="50" align="center" />
          <el-table-column prop="invoiceCode" label="Mã phiếu thu" width="160" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="row.invoiceCode" size="mini" type="info">{{ row.invoiceCode }}</el-tag>
              <span v-else style="color: #94a3b8; font-style: italic; font-size: 11px;">(Tạo mới)</span>
            </template>
          </el-table-column>
          <el-table-column prop="studentCode" label="Mã HS" width="105" align="center">
            <template slot-scope="{ row }">
              <span style="font-weight: 600;">{{ row.studentCode }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="fullName" label="Họ và tên" min-width="150" />
          <el-table-column prop="className" label="Lớp" width="110" align="center" />

          <el-table-column prop="beforeMoney" label="Thiếu/thừa phiếu thu trước" width="170" align="right">
            <template slot-scope="{ row }">
              <span :style="{ color: row.beforeMoney < 0 ? '#dc2626' : (row.beforeMoney > 0 ? '#16a34a' : '#64748b'), fontWeight: '600' }">
                {{ formatMoney(row.beforeMoney) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="sumMoney" label="Phải thu" width="120" align="right">
            <template slot-scope="{ row }">
              <span style="font-weight: 600; color: #ea580c;">{{ formatMoney(row.sumMoney) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="collectedMoney" label="Đã thu" width="110" align="right">
            <template slot-scope="{ row }">
              <span style="color: #16a34a;">{{ formatMoney(row.collectedMoney) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="afterMoney" label="Còn lại" width="120" align="right">
            <template slot-scope="{ row }">
              <span :style="{ color: row.afterMoney < 0 ? '#dc2626' : '#16a34a', fontWeight: '600' }">
                {{ formatMoney(row.afterMoney) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="note" label="Ghi chú duy nhất" min-width="150" show-overflow-tooltip />
        </el-table>
      </div>
    </div>

    <!-- Modal Thêm Khoản Thu Mới: Chuẩn hóa theo guidelines.md -->
    <el-dialog
      :visible.sync="customFeeDialogVisible"
      width="580px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="sk-dialog custom-fee-add-dialog"
    >
      <div slot="title" class="custom-fee-dialog-header">
        <i class="el-icon-circle-plus custom-fee-header-icon"></i>
        <div>
          <div class="custom-fee-header-title">Thêm Khoản Thu Mới Vào File Mẫu & Danh Mục</div>
          <div class="custom-fee-header-subtitle">Khoản thu này sẽ được lưu vào danh mục Sheet 'Khoản Thu' và hiển thị ở cuối Sheet 'Danh Sách HS'</div>
        </div>
      </div>

      <div class="custom-fee-body">
        <!-- Panel 1: Mã hệ thống tự động sinh (Disabled - không cho sửa) -->
        <div class="sk-panel" style="background: #f1f5f9; padding: 12px 14px;">
          <div class="sk-panel-title" style="font-size: 13px; margin-bottom: 8px;">
            <i class="el-icon-lock"></i> Mã hệ thống tự động sinh (Cố định, không chỉnh sửa)
          </div>
          <div style="display: flex; gap: 12px;">
            <div style="flex: 1;">
              <span class="custom-fee-label">Mã khoản thu (revenueCode &le; 10 ký tự):</span>
              <el-input v-model="customFeeForm.revenueCode" disabled size="small" prefix-icon="el-icon-key" />
            </div>
            <div style="flex: 1.4;">
              <span class="custom-fee-label">Mã định danh hệ thống (idx &le; 30 ký tự):</span>
              <el-input v-model="customFeeForm.idx" disabled size="small" prefix-icon="el-icon-document" />
            </div>
          </div>
        </div>

        <!-- Panel 2: Thông tin cấu hình khoản thu -->
        <div class="sk-panel" style="margin-top: 12px; padding: 12px 14px;">
          <div class="sk-panel-title" style="font-size: 13px; margin-bottom: 8px;">
            <i class="el-icon-edit-outline"></i> Thông tin cấu hình khoản thu
          </div>

          <div style="margin-bottom: 12px;">
            <span class="custom-fee-label">Tên khoản thu <span style="color: red;">*</span>:</span>
            <el-input
              v-model="customFeeForm.name"
              placeholder="Ví dụ: Phụ phí dã ngoại, Quỹ học phẩm, Đồng phục..."
              size="small"
              clearable
            />
          </div>

          <div style="display: flex; gap: 12px; margin-bottom: 12px;">
            <div style="flex: 1;">
              <span class="custom-fee-label">Đơn vị tính:</span>
              <el-input
                v-model="customFeeForm.unit"
                placeholder="Buổi, Tháng, Lần, Chiếc..."
                size="small"
              />
            </div>
            <div style="flex: 1;">
              <span class="custom-fee-label">Số lượng mặc định:</span>
              <el-input-number
                v-model="customFeeForm.quantity"
                :step="1"
                size="small"
                style="width: 100%;"
              />
            </div>
          </div>

          <div style="display: flex; gap: 12px; margin-bottom: 12px;">
            <div style="flex: 1;">
              <span class="custom-fee-label">Đơn giá mặc định (đ):</span>
              <el-input-number
                v-model="customFeeForm.price"
                :min="0"
                :step="50000"
                size="small"
                style="width: 100%;"
              />
            </div>
            <div style="flex: 1;">
              <span class="custom-fee-label">Giảm giá mặc định (đ):</span>
              <el-input-number
                v-model="customFeeForm.discountNumber"
                :min="0"
                :step="10000"
                size="small"
                style="width: 100%;"
              />
            </div>
          </div>

          <!-- Panel 3: Thành tiền tự động tính -->
          <div class="custom-fee-calc-box">
            <div class="calc-box-left">
              <span class="calc-box-formula">
                Công thức: {{ customFeeForm.quantity !== undefined && customFeeForm.quantity !== null && customFeeForm.quantity !== '' ? customFeeForm.quantity : 1 }} (SL) &times; {{ formatMoney(customFeeForm.price) }} (Đơn giá) &minus; {{ formatMoney(customFeeForm.discountNumber) }} (Giảm giá)
              </span>
              <span class="calc-box-text">Thành tiền dự tính:</span>
            </div>
            <div class="calc-box-amount">
              {{ formatMoney(customFeeAmount) }} đ
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="sk-dialog__footer">
        <el-button class="btn-cancel" plain @click="customFeeDialogVisible = false">Hủy</el-button>
        <el-button class="btn-save" type="success" icon="el-icon-check" @click="confirmAddCustomFee">
          Thêm vào danh mục
        </el-button>
      </div>
    </el-dialog>

    <!-- Footer thanh điều hướng Wizard chuẩn hóa (Nút Đóng ở bên phải cùng) -->
    <div slot="footer" class="sk-dialog__footer wizard-footer-redesign">
      <div
        v-if="activeStep === 0 && (step1TemplateDataLoading || studentLoading || loadingBenchmark)"
        class="wizard-source-data-status"
        role="status"
        aria-live="polite"
      >
        <i class="el-icon-loading"></i>
        Đang lấy dữ liệu gốc, vui lòng chờ...
      </div>
      <div class="footer-right">
        <el-button
          v-if="activeStep > 0"
          size="small"
          class="btn-wizard-prev"
          icon="el-icon-arrow-left"
          plain
          @click="handlePrevStep"
        >
          Quay lại Bước {{ activeStep }}
        </el-button>
        <el-button
          v-if="activeStep === 0"
          size="small"
          class="btn-wizard-next"
          type="primary"
          :loading="step1TemplateDataLoading || studentLoading || loadingBenchmark"
          :disabled="!step1TemplateDataReady || studentLoading || loadingBenchmark"
          @click="handleNextStep1"
        >
          Tiếp tục sang Bước 2 <i class="el-icon-arrow-right"></i>
        </el-button>
        <el-button
          v-if="activeStep === 1"
          size="small"
          class="btn-wizard-next"
          type="primary"
          :loading="checkingOnlinePayment"
          :disabled="!canProceedStep2 || checkingOnlinePayment"
          @click="handleNextStep2"
        >
          Tiếp tục sang Bước 3 <i class="el-icon-arrow-right"></i>
        </el-button>
        <el-button
          v-if="activeStep === 2"
          size="small"
          class="btn-wizard-commit"
          type="success"
          icon="el-icon-check"
          :loading="savingInvoices"
          :disabled="savingInvoices"
          @click="handleCommitInvoicesToBE"
        >
          Khởi tạo phiếu thu vào hệ thống
        </el-button>
        <!-- Nút ĐÓNG Ở BÊN PHẢI CÙNG -->
        <el-button
          size="small"
          class="btn-wizard-close"
          plain
          icon="el-icon-close"
          @click="closeDialog"
        >
          Đóng
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import moment from "moment";
import * as XLSX from "xlsx";
import TuitionDraftInvoiceService from "@/services/tutition_v3/TuitionDraftInvoiceService";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import http from "@/http-common";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { kidStatusList } from "@/constant/listCommon";
import { downloadInvoiceTemplateWithItemsAtEnd } from "@/commonFuncion/downloadInvoiceTemplateWithItemsAtEnd";
import {
  readInvoiceExcelWorkbook,
  inspectInvoiceExcelV2Layout,
  prepareInvoiceExcelLayout,
  resolveInvoiceExcelV2DetailIdx,
  hasCompleteInvoiceExcelV2Details,
} from "@/commonFuncion/invoiceExcelImportLayout";

const STATUS_MAP = {
  UNBILLED: "Chưa có phiếu thu",
  UNPAID: "Chưa đóng 100%",
  PARTIALLY_PAID: "Chưa đóng đủ",
  FULLY_PAID: "Đóng đủ 100%",
  OVERPAID: "Đóng thừa",
  FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
  PAID_WITH_DEFICIT: "Đóng đủ thiếu",
};

/**
 * Sinh chuỗi ngẫu nhiên ký tự a-z, A-Z, 0-9
 */
function generateRandomString(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export default {
  name: "InvoiceExcelImportWizardDialog",
  props: {
    gradeList: { type: Array, default: () => [] },
    classList: { type: Array, default: () => [] },
    kidGroupList: { type: Array, default: () => [] },
  },

  data() {
    return {
      dialogVisible: false,
      activeStep: 0,
      kidStatusList,

      // --- BƯỚC 1: CHỌN HỌC SINH & TẢI MẪU ---
      step1Filter: {
        targetType: "CLASS",
        idGrade: null,
        idClass: null,
        idGroup: null,
        kidStatus: "STUDYING",
        monthAndYear: `${moment().month() + 1}-${moment().year()}`,
        turn: 1,
      },
      // Tùy chọn điểm danh và thiếu/thừa trước
      step1Options: {
        includeAttendance: true,
        attendanceMonth: `${moment().subtract(1, "month").month() + 1}-${moment().subtract(1, "month").year()}`,
        includePreviousDebt: true,
      },
      studentList: [],
      selectedStudents: [],
      feeCatalogRefreshTimer: null,
      feeCatalogRequestSeq: 0,
      step1TemplateDataReady: false,
      step1TemplateDataLoading: false,
      studentLoading: false,
      studentKeyword: "",
      downloadingTemplate: false,
      loadingBenchmark: false,
      benchmarkData: null,
      scopeName: "",
      externalFeeColumns: [],

      // Danh mục khoản thu cho template (Sheet 2)
      configuredFeeColumns: [],
      systemFeeColumns: [],
      importedTemplateFees: [],
      manuallyAddedFees: [],
      uploadedTemplateCatalogFile: null,
      isTemplateCatalogDragOver: false,
      useSystemFees: true,
      useTemplateCatalogFees: true,
      customFeeDialogVisible: false,
      customFeeForm: {
        name: "",
        revenueCode: "",
        idx: "",
        unit: "Buổi",
        quantity: 1,
        price: 0,
        discountNumber: 0,
      },

      // --- BƯỚC 2: CẤU HÌNH ĐỢT THU & UPLOAD ---
      invoicePeriod: {
        dateRange: [],
        description: "",
        turn: 1,
        monthAndYear: "",
      },
      isDragOver: false,
      uploadedFile: null,
      parsedRows: [],
      detectedFeeColumns: [],
      hasFeeSheet: false,
      feeCatalogMap: new Map(), // Tra cứu từ Sheet 2
      rawMatrix: null,
      rawSheet2Rows: [],
      rawWorkbookMetadata: null,
      resolvedExcelV2Catalog: new Map(),
      hasValidated: false,
      errorList: [],
      checkingOnlinePayment: false,
      onlinePaymentValidated: false,

      // --- BƯỚC 3: XEM TRƯỚC DỮ LIỆU ---
      generatedDbInvoices: [],
      savingInvoices: false,
    };
  },

  computed: {
    /** Tháng điểm danh do người dùng chọn, độc lập với kỳ thu. */
    attendancePickerOptions() {
      // Tính toán động theo ô tháng của đợt thu (step1Filter.monthAndYear)
      let feeMonthMoment = moment();
      if (this.step1Filter.monthAndYear) {
        const [m, y] = String(this.step1Filter.monthAndYear).split("-");
        if (m && y) {
          feeMonthMoment = moment(`${y}-${String(m).padStart(2, "0")}-01`, "YYYY-MM-DD");
        }
      }

      const curM = feeMonthMoment.month() + 1;
      const curY = feeMonthMoment.year();

      const prevMoment = feeMonthMoment.clone().subtract(1, "month");
      const prevM = prevMoment.month() + 1;
      const prevY = prevMoment.year();

      const shortcutMonths = [
        { label: `Tháng đợt thu (T${curM}/${curY})`, value: feeMonthMoment },
        { label: `Tháng trước đợt thu (T${prevM}/${prevY})`, value: prevMoment },
      ];

      return {
        shortcuts: shortcutMonths.map((item) => ({
          text: item.label,
          onClick(picker) {
            picker.$emit("pick", item.value.toDate());
          },
        })),
      };
    },

    /** Cấu hình phím tắt cho khoảng thời gian hiệu lực ở Bước 2, tự động theo tháng đợt thu */
    pickerOptions() {
      let feeMoment = moment();
      const my = (this.invoicePeriod && this.invoicePeriod.monthAndYear) || (this.step1Filter && this.step1Filter.monthAndYear);
      if (my) {
        const [m, y] = String(my).split("-");
        if (m && y) {
          feeMoment = moment(`${y}-${String(m).padStart(2, "0")}-01`, "YYYY-MM-DD");
        }
      }
      const curM = feeMoment.month() + 1;
      const curY = feeMoment.year();

      const nextMoment = feeMoment.clone().add(1, "month");
      const nextM = nextMoment.month() + 1;
      const nextY = nextMoment.year();

      return {
        shortcuts: [
          {
            text: `Tháng đợt thu (T${curM}/${curY})`,
            onClick(picker) {
              const start = feeMoment.clone().startOf("month").toDate();
              const end = feeMoment.clone().endOf("month").toDate();
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: `Tháng sau (T${nextM}/${nextY})`,
            onClick(picker) {
              const start = nextMoment.clone().startOf("month").toDate();
              const end = nextMoment.clone().endOf("month").toDate();
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      };
    },

    filteredClassList() {
      if (!this.step1Filter.idGrade) return this.classList;
      return this.classList.filter((c) => c.idGrade === this.step1Filter.idGrade);
    },

    currentClassName() {
      if (this.scopeName) return this.scopeName;
      if (this.step1Filter.targetType === "CLASS") {
        const cls = this.classList.find((c) => c.id === this.step1Filter.idClass);
        return cls ? cls.className : "";
      } else if (this.step1Filter.targetType === "GROUP") {
        const grp = this.kidGroupList.find((g) => g.id === this.step1Filter.idGroup);
        return grp ? grp.nameGroup : "";
      }
      return "";
    },

    filteredStudents() {
      if (!this.studentKeyword) return this.studentList;
      const kw = this.studentKeyword.toLowerCase().trim();
      return this.studentList.filter((s) => {
        const name = (s.fullName || s.name || "").toLowerCase();
        const code = (s.studentCode || s.kidCode || "").toLowerCase();
        return name.includes(kw) || code.includes(kw);
      });
    },

    validRowCount() {
      const errorRows = new Set(this.errorList.map((e) => e.row));
      return Math.max(0, this.parsedRows.length - errorRows.size);
    },

    canProceedStep2() {
      return (
        this.hasValidated &&
        this.parsedRows.length > 0 &&
        this.errorList.length === 0 &&
        !this.checkingOnlinePayment &&
        this.onlinePaymentValidated
      );
    },

    totalSumMoney() {
      return this.generatedDbInvoices.reduce((sum, inv) => sum + (Number(inv.sumMoney) || 0), 0);
    },

    totalCollectedMoney() {
      return this.generatedDbInvoices.reduce((sum, inv) => sum + (Number(inv.collectedMoney) || 0), 0);
    },

    totalAfterMoney() {
      return this.generatedDbInvoices.reduce((sum, inv) => sum + (Number(inv.afterMoney) || 0), 0);
    },

    totalBeforeMoney() {
      return this.generatedDbInvoices.reduce((sum, inv) => sum + (Number(inv.beforeMoney) || 0), 0);
    },

    customFeeAmount() {
      const rawQty = this.customFeeForm.quantity;
      const qty = rawQty !== undefined && rawQty !== null && rawQty !== "" ? Number(rawQty) : 1;
      const price = Number(this.customFeeForm.price || 0);
      const discount = Number(this.customFeeForm.discountNumber || 0);
      return qty * price - discount;
    },

  },

  methods: {
    formatDate(date) {
      if (!date) return "—";
      return moment(date).format("DD/MM/YYYY");
    },

    formatMoney(val) {
      return Number(val || 0).toLocaleString("vi-VN");
    },


    getStatusTagType(status) {
      if (status === "FULLY_PAID") return "success";
      if (status === "PARTIALLY_PAID") return "warning";
      if (status === "PAID_WITH_DEFICIT" || status === "UNPAID") return "danger";
      return "info";
    },

    getStatusLabel(status) {
      return STATUS_MAP[status] || status;
    },

    /**
     * Mở dialog với ngữ cảnh & danh sách học sinh từ bộ lọc ngoài
     */
    async open(context = {}) {
      this.resetState();
      this.dialogVisible = true;
      this.activeStep = 0;
      this.hasValidated = false;
      this.errorList = [];
      this.uploadedFile = null;
      this.parsedRows = [];
      this.generatedDbInvoices = [];
      this.benchmarkData = null;
      this.loadingBenchmark = false;

      this.scopeName = context.scopeName || "";
      this.externalFeeColumns = Array.isArray(context.externalFeeColumns) ? context.externalFeeColumns : [];
      this.step1Filter.targetType = context.targetType === "GROUP" ? "GROUP" : "CLASS";
      this.step1Filter.idGrade = context.idGrade || null;
      this.step1Filter.idClass = context.idClass || null;
      this.step1Filter.idGroup = context.idGroup || null;

      // 4. Disable chọn tháng, nếu ở màn ngoài có tháng thì lấy
      let feeM = moment().month() + 1;
      let feeY = moment().year();
      if (context.month && context.year) {
        feeM = Number(context.month);
        feeY = Number(context.year);
      } else if (context.monthAndYear) {
        const parts = String(context.monthAndYear).split("-");
        if (parts.length >= 2) {
          feeM = Number(parts[0]);
          feeY = Number(parts[1]);
        }
      }

      const my = `${feeM}-${feeY}`;
      this.step1Filter.monthAndYear = my;
      this.invoicePeriod.monthAndYear = my;
      this.invoicePeriod.description = `Phiếu thu học phí tháng ${String(feeM).padStart(2, "0")}.${feeY}`;

      // Khoảng thời gian hiệu lực mặc định theo tháng được chọn (Từ ngày đầu tháng đến cuối tháng)
      const feeMoment = moment(`${feeY}-${String(feeM).padStart(2, "0")}-01`, "YYYY-MM-DD");
      this.invoicePeriod.dateRange = [
        feeMoment.clone().startOf("month").format("YYYY-MM-DD"),
        feeMoment.clone().endOf("month").format("YYYY-MM-DD"),
      ];

      // Tháng điểm danh: Tự động lùi về 1 tháng so với ô tháng của đợt thu
      const prevMonthMoment = feeMoment.clone().subtract(1, "month");
      this.step1Options.attendanceMonth = `${prevMonthMoment.month() + 1}-${prevMonthMoment.year()}`;

      // 4. Nếu ở màn ngoài không có đợt thì là đợt 1, nếu có đợt ghi nhận đợt của filter màn ngoài.
      const outsideTurn = context.turn ? Number(context.turn) : 1;
      this.step1Filter.turn = outsideTurn;
      this.invoicePeriod.turn = outsideTurn;

      // 4. Danh sách học sinh lấy từ màn filter bên ngoài vào
      if (Array.isArray(context.candidateStudents) && context.candidateStudents.length > 0) {
        this.studentList = context.candidateStudents.map((item) => ({
          ...item,
          studentCode: item.studentCode || item.kidCode || "",
          fullName: item.fullName || item.name || "",
          className: item.className || this.scopeName || this.currentClassName,
          invoiceDetails: Array.isArray(item.invoiceDetails) ? item.invoiceDetails : [],
        }));
        this.selectedStudents = [...this.studentList];
        this.selectAllStudents();
      } else {
        this.studentList = [];
        this.selectedStudents = [];
        await this.fetchKidsInDraft();
      }

      // Lấy danh mục sau khi đã biết danh sách học sinh của phạm vi.
      this.fetchDefaultFeeColumns();
    },

    resetState() {
      clearTimeout(this.feeCatalogRefreshTimer);
      this.feeCatalogRefreshTimer = null;
      this.feeCatalogRequestSeq += 1;
      this.step1TemplateDataReady = false;
      this.step1TemplateDataLoading = false;

      this.activeStep = 0;
      this.hasValidated = false;
      this.errorList = [];
      this.uploadedFile = null;
      this.parsedRows = [];
      this.detectedFeeColumns = [];
      this.hasFeeSheet = false;
      if (this.feeCatalogMap && typeof this.feeCatalogMap.clear === "function") {
        this.feeCatalogMap.clear();
      } else {
        this.feeCatalogMap = new Map();
      }
      this.checkingOnlinePayment = false;
      this.onlinePaymentValidated = false;

      this.studentList = [];
      this.selectedStudents = [];
      this.studentKeyword = "";
      this.studentLoading = false;
      this.downloadingTemplate = false;
      this.loadingBenchmark = false;
      this.benchmarkData = null;
      this.configuredFeeColumns = [];
      this.systemFeeColumns = [];
      this.importedTemplateFees = [];
      this.manuallyAddedFees = [];
      this.uploadedTemplateCatalogFile = null;
      this.isTemplateCatalogDragOver = false;
      if (this.$refs.templateCatalogFileInput) {
        this.$refs.templateCatalogFileInput.value = "";
      }
      this.useSystemFees = true;
      this.useTemplateCatalogFees = true;
      this.customFeeDialogVisible = false;
      this.customFeeForm = {
        name: "",
        revenueCode: "",
        idx: "",
        unit: "Buổi",
        quantity: 1,
        price: 0,
        discountNumber: 0,
      };

      this.invoicePeriod = {
        dateRange: [],
        description: "",
        turn: 1,
        monthAndYear: "",
      };

      this.generatedDbInvoices = [];
      this.savingInvoices = false;
      this.rawMatrix = null;
      this.rawSheet2Rows = [];
      this.rawWorkbookMetadata = null;
      this.resolvedExcelV2Catalog = new Map();

      if (this.$refs.excelFileInput) {
        this.$refs.excelFileInput.value = "";
      }
      if (this.$refs.studentTable) {
        this.$refs.studentTable.clearSelection();
      }
    },

    handleBeforeClose(done) {
      this.closeDialog();
      if (typeof done === "function") {
        done();
      }
    },

    closeDialog() {
      this.dialogVisible = false;
      this.resetState();
      this.$emit("dialog-close");
    },

    async handleTargetTypeChange() {
      this.step1Filter.idClass = null;
      this.step1Filter.idGroup = null;
      await this.fetchKidsInDraft();
      this.fetchDefaultFeeColumns();
    },

    handleGradeChange() {
      this.step1Filter.idClass = null;
      this.fetchKidsInDraft();
    },

    async handleMonthChange(val) {
      if (val) {
        this.invoicePeriod.monthAndYear = val;
        const [m, y] = val.split("-");
        const feeMoment = moment(`${y}-${String(m).padStart(2, "0")}-01`, "YYYY-MM-DD");
        const prevM = feeMoment.clone().subtract(1, "month");
        this.step1Options.attendanceMonth = `${prevM.month() + 1}-${prevM.year()}`;
        this.invoicePeriod.description = `Phiếu thu học phí tháng ${String(m).padStart(2, "0")}.${y}`;
        this.invoicePeriod.dateRange = [
          feeMoment.clone().startOf("month").format("YYYY-MM-DD"),
          feeMoment.clone().endOf("month").format("YYYY-MM-DD"),
        ];
      }
      await this.fetchKidsInDraft();
      this.fetchDefaultFeeColumns();
    },

    handleTurnChange(val) {
      this.invoicePeriod.turn = val;
      this.fetchDefaultFeeColumns();
    },

    /**
     * Tải danh sách học sinh theo phạm vi lớp hoặc nhóm
     */
    async fetchKidsInDraft() {
      const targetId =
        this.step1Filter.targetType === "CLASS"
          ? this.step1Filter.idClass
          : this.step1Filter.idGroup;

      if (!targetId) {
        this.studentList = [];
        this.selectedStudents = [];
        return;
      }

      const [month, year] = String(this.step1Filter.monthAndYear).split("-");

      this.studentLoading = true;
      try {
        const res = await TuitionDraftInvoiceService.getKidsInDraft({
          status: this.step1Filter.kidStatus,
          month: Number(month),
          year: Number(year),
          targetId,
          targetType: this.step1Filter.targetType,
        });

        const payload = res?.data?.data ?? res?.data;
        const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.content) ? payload.content : []);
        this.studentList = list.map((item) => ({
          ...item,
          studentCode: item.studentCode || item.kidCode || "",
          fullName: item.fullName || item.name || "",
          className: item.className || this.currentClassName,
          invoiceDetails: Array.isArray(item.invoiceDetails) ? item.invoiceDetails : [],
        }));
        this.selectedStudents = [...this.studentList];
        this.selectAllStudents();
      } catch (err) {
        console.error(err);
        this.studentList = [];
        this.selectedStudents = [];
      } finally {
        this.studentLoading = false;
      }
    },

    /**
     * API 1: Lấy danh mục khoản thu áp dụng từ hệ thống theo bộ lọc
     * POST /receipt/v1/invoice/excel-import/step1/fee-catalog
     */
    /**
     * API 1 + API 2: Lấy các khoản thu từ phiếu thu thực tế của học sinh được chọn
     */
    async fetchDefaultFeeColumns() {
      const requestSeq = ++this.feeCatalogRequestSeq;
      this.step1TemplateDataReady = false;
      this.step1TemplateDataLoading = false;
      const targetId =
        this.step1Filter.targetType === "CLASS"
          ? this.step1Filter.idClass
          : this.step1Filter.idGroup;

      if (!targetId || this.selectedStudents.length === 0) {
        this.configuredFeeColumns = this.configuredFeeColumns.filter((fee) => fee.isCustom);
        return;
      }

      const [month, year] = String(this.step1Filter.monthAndYear).split("-");
      const listIdKids = Array.from(new Set(this.selectedStudents.map((s) => s.idStudent ?? s.id).filter(Boolean)));
      const listInvoiceCodes = this.selectedStudents
        .map((s) => ({ idKid: s.idStudent ?? s.id, invoiceCode: String(s.invoiceCode || "").trim() }))
        .filter((item) => item.idKid && item.invoiceCode && item.invoiceCode !== "-");

      this.step1TemplateDataLoading = true;
      try {
        // 1. Danh mục khoản thu từ phiếu active của học sinh được chọn.
        const catalogPromise = ManagingStudentInvoiceService.getExcelImportStep1FeeCatalog({
          targetType: this.step1Filter.targetType,
          targetId: Number(targetId),
          month: Number(month),
          year: Number(year),
          turn: Number(this.step1Filter.turn || 1),
          listIdKids,
        });

        // 2. Dữ liệu từng học sinh cần mã phiếu để lấy đúng các khoản đã lưu.
        const templatePayload = {
          targetType: this.step1Filter.targetType,
          targetId: Number(targetId),
          month: Number(month),
          year: Number(year),
          turn: Number(this.step1Filter.turn || 1),
          includeAttendance: Boolean(this.step1Options.includeAttendance),
          includePreviousDebt: Boolean(this.step1Options.includePreviousDebt),
          listIdKids,
          listInvoiceCodes,
        };
        if (this.step1Options.includeAttendance) {
          templatePayload.attendanceMonth = String(this.step1Options.attendanceMonth);
        }
        const templatePromise = ManagingStudentInvoiceService.getExcelImportStep1TemplateData(templatePayload)
          .catch((tErr) => {
            console.warn("Chưa tải được template-data phụ trợ ở Bước 1:", tErr);
            return null;
          });

        const [catalogResult, templateResult] = await Promise.allSettled([catalogPromise, templatePromise]);
        if (requestSeq !== this.feeCatalogRequestSeq) return;
        if (catalogResult.status === "rejected") throw catalogResult.reason;
        const catRes = catalogResult.value;
        const tRes = templateResult.value;

        let feeList = [];
        if (catRes && catRes.data && Array.isArray(catRes.data.data)) {
          feeList = [...catRes.data.data];
        }

        // Bổ sung khoản từ phiếu được chọn nếu API 2 trả thêm cột.
        const tData = tRes && tRes.data && tRes.data.data;
        if (tData && Array.isArray(tData.feeColumns)) {
          this.benchmarkData = this.buildBenchmarkData(tData);
          const existingCodes = new Set(feeList.map((f) => f.revenueCode));
          for (const extraFee of tData.feeColumns) {
            if (extraFee && extraFee.revenueCode && !existingCodes.has(extraFee.revenueCode)) {
              feeList.push(extraFee);
              existingCodes.add(extraFee.revenueCode);
            }
          }
        }


        if (feeList.length > 0) {
          const systemFees = feeList.map((f) => ({
            name: f.revenueName || f.name,
            revenueName: f.revenueName || f.name,
            revenueCode: f.revenueCode, // Giữ nguyên độ dài (có thể đến 15 ký tự)
            idx: f.idx, // Giữ nguyên UUID 36 ký tự
            unit: f.unit || "Tháng",
            quantity: 1,
            price: f.price || 0,
            discountNumber: 0,
            source: f.source || "Phiếu thu đã phát hành",
            description: f.description || "",
            isCustom: false,
            isExternal: true, // Khoản thu từ hệ thống -> KHÔNG ĐƯỢC XÓA
          }));
          this.systemFeeColumns = systemFees;
        } else {
          this.systemFeeColumns = [];
        }
        this.recomputeConfiguredFees();
        this.step1TemplateDataReady = Boolean(tData);
      } catch (err) {
        if (requestSeq !== this.feeCatalogRequestSeq) return;
        console.error("Lỗi lấy danh mục khoản thu từ phiếu thu:", err);
        let msg = (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          "Không thể tải danh mục khoản thu từ phiếu thu.";
        if (typeof msg === "object") {
          try {
            msg = JSON.stringify(msg);
          } catch (e) {
            msg = "Không thể tải danh mục khoản thu từ phiếu thu.";
          }
        }
        NotifyMessage.leftCornerNotificationError(msg);
        this.systemFeeColumns = [];
        this.recomputeConfiguredFees();
      } finally {
        if (requestSeq === this.feeCatalogRequestSeq) {
          this.step1TemplateDataLoading = false;
        }
      }
    },

    handleStudentSelectionChange(selection) {
      this.selectedStudents = selection;
      this.step1TemplateDataReady = false;
      this.step1TemplateDataLoading = selection.length > 0;
      this.feeCatalogRequestSeq += 1;
      clearTimeout(this.feeCatalogRefreshTimer);
      this.feeCatalogRefreshTimer = setTimeout(() => this.fetchDefaultFeeColumns(), 150);
    },

    getStudentRowKey(row) {
      return row.idStudent || row.idKid || row.id || row.studentCode || row.kidCode;
    },

    selectAllStudents() {
      this.selectedStudents = [...this.studentList];
      const performSelection = () => {
        const table = this.$refs.studentTable;
        if (table && this.studentList.length > 0) {
          table.clearSelection();
          this.studentList.forEach((row) => {
            table.toggleRowSelection(row, true);
          });
        }
      };

      this.$nextTick(performSelection);
      setTimeout(performSelection, 150);
    },

    clearStudentSelection() {
      this.selectedStudents = [];
      this.$refs.studentTable?.clearSelection();
    },

    /**
     * Mở dialog thêm khoản thu mới:
     * - revenueCode: Mã khoản thu (<= 10 ký tự)
     * - idx: Mã định danh hệ thống (<= 30 ký tự)
     */
    openAddCustomFeeDialog() {
      this.customFeeForm = {
        name: "",
        revenueCode: `KT${generateRandomString(6)}`, // Mã khoản thu <= 10 ký tự
        idx: generateRandomString(28), // Mã định danh hệ thống <= 30 ký tự
        unit: "Buổi",
        quantity: 1,
        price: 0,
        discountNumber: 0,
      };
      this.customFeeDialogVisible = true;
    },

    confirmAddCustomFee() {
      const name = this.customFeeForm.name.trim();
      if (!name) {
        NotifyMessage.leftCornerNotificationError("Vui lòng nhập tên khoản thu.");
        return;
      }

      const nameNorm = name.toLowerCase();
      const codeNorm = (this.customFeeForm.revenueCode || "").trim().toUpperCase();

      const isDuplicate = this.configuredFeeColumns.some(
        (f) => (f.name || f.revenueName || "").trim().toLowerCase() === nameNorm ||
               (f.revenueCode && f.revenueCode.trim().toUpperCase() === codeNorm)
      );

      if (isDuplicate) {
        NotifyMessage.leftCornerNotificationError("Khoản thu này đã tồn tại trong danh mục!");
        return;
      }

      const rawQty = this.customFeeForm.quantity;
      const quantity = rawQty !== undefined && rawQty !== null && rawQty !== "" ? Number(rawQty) : 1;

      const newFee = {
        name,
        revenueName: name,
        revenueCode: this.customFeeForm.revenueCode, // Mã khoản thu
        idx: this.customFeeForm.idx, // Mã định danh
        unit: this.customFeeForm.unit || "Buổi",
        quantity,
        price: Number(this.customFeeForm.price || 0),
        discountNumber: Number(this.customFeeForm.discountNumber || 0),
        amount: this.customFeeAmount,
        source: "Tự thêm",
        isCustom: true, // Khoản tự thêm mới -> được phép xóa
        isExternal: false,
      };

      this.manuallyAddedFees.push(newFee);
      this.recomputeConfiguredFees();

      this.customFeeDialogVisible = false;
      NotifyMessage.leftCornerNotificationSuccess(`Đã thêm khoản thu "${name}" (Mã: ${this.customFeeForm.revenueCode}) vào file mẫu.`);
    },

    /**
     * Yêu cầu 1: Các khoản thu từ màn chính không được phép xóa ở danh mục khoản thu
     */
    removeConfiguredFee(index) {
      const fee = this.configuredFeeColumns[index];
      if (!fee || !fee.isCustom) {
        NotifyMessage.leftCornerNotificationWarning("Khoản thu này được kế thừa từ màn hình chính, không thể xóa!");
        return;
      }

      // Xóa khỏi manuallyAddedFees nếu có
      const mIdx = this.manuallyAddedFees.findIndex(
        (f) => f.revenueCode === fee.revenueCode || (f.name && f.name.toLowerCase() === (fee.name || "").toLowerCase())
      );
      if (mIdx >= 0) {
        this.manuallyAddedFees.splice(mIdx, 1);
      }

      // Xóa khỏi importedTemplateFees nếu có
      const impIdx = this.importedTemplateFees.findIndex(
        (f) => f.revenueCode === fee.revenueCode || (f.name && f.name.toLowerCase() === (fee.name || "").toLowerCase())
      );
      if (impIdx >= 0) {
        this.importedTemplateFees.splice(impIdx, 1);
      }

      this.recomputeConfiguredFees();
      NotifyMessage.leftCornerNotificationSuccess(`Đã xóa khoản thu "${fee.name}" khỏi danh mục.`);
    },

    /**
     * Thuật toán Recompute & Merge danh mục khoản thu không bị trùng lặp:
     * - Kết hợp các nguồn theo tùy chọn: Hệ thống (useSystemFees), File mẫu (useTemplateCatalogFees), Tự thêm
     * - Khử trùng theo cả Mã khoản thu (revenueCode) và Tên khoản thu (name)
     * - Ưu tiên giữ khoản chuẩn từ Hệ thống
     */
    recomputeConfiguredFees() {
      const mergedList = [];
      const seenRevenueCodes = new Set();
      const seenNames = new Set();

      const normalizeName = (name) => String(name || "").trim().toLowerCase();
      const normalizeCode = (code) => String(code || "").trim().toUpperCase();

      const addFee = (fee, sourceLabel, isDeletable = false) => {
        const code = normalizeCode(fee.revenueCode);
        const name = normalizeName(fee.name || fee.revenueName);

        // Khử trùng lặp
        if (code && seenRevenueCodes.has(code)) {
          return false;
        }
        if (name && seenNames.has(name)) {
          return false;
        }

        if (code) seenRevenueCodes.add(code);
        if (name) seenNames.add(name);

        mergedList.push({
          ...fee,
          name: fee.name || fee.revenueName,
          revenueName: fee.revenueName || fee.name,
          source: sourceLabel || fee.source || "Hệ thống",
          isCustom: isDeletable,
        });
        return true;
      };

      // 1. Nguồn Hệ thống (Ưu tiên chuẩn DB trước)
      if (this.useSystemFees && Array.isArray(this.systemFeeColumns)) {
        this.systemFeeColumns.forEach((f) => {
          addFee(f, f.source || "Hệ thống", false);
        });
      }

      // 2. Nguồn từ File mẫu tải lên (Tự động bỏ qua các khoản trùng lặp với hệ thống)
      if (this.useTemplateCatalogFees && Array.isArray(this.importedTemplateFees)) {
        this.importedTemplateFees.forEach((f) => {
          addFee(f, "Từ file mẫu", true);
        });
      }

      // 3. Nguồn Tự thêm mới bằng tay
      if (Array.isArray(this.manuallyAddedFees)) {
        this.manuallyAddedFees.forEach((f) => {
          addFee(f, "Tự thêm", true);
        });
      }

      this.configuredFeeColumns = mergedList;
    },

    /**
     * Xử lý kéo thả file Excel mẫu tại Bước 1
     */
    handleTemplateCatalogDrop(e) {
      this.isTemplateCatalogDragOver = false;
      const files = e.dataTransfer && e.dataTransfer.files;
      if (files && files.length > 0) {
        this.processTemplateCatalogFile(files[0]);
      }
    },

    /**
     * Xử lý chọn file Excel mẫu từ input file tại Bước 1
     */
    handleTemplateCatalogUpload(e) {
      const files = e.target && e.target.files;
      if (files && files.length > 0) {
        this.processTemplateCatalogFile(files[0]);
      }
    },

    /**
     * Hàm dùng chung xử lý đọc file mẫu (hỗ trợ cả chọn file và kéo thả)
     */
    processTemplateCatalogFile(file) {
      if (!file) return;

      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "xlsx") {
        NotifyMessage.leftCornerNotificationError("Vui lòng chọn file mẫu Excel (.xlsx)!");
        return;
      }

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          const workbookInfo = readInvoiceExcelWorkbook(workbook, (sheet) => XLSX.utils.sheet_to_json(sheet, { header: 1 }));
          const structure = inspectInvoiceExcelV2Layout(workbookInfo.matrix, workbookInfo.metadata);
          if (structure.errors.length) {
            NotifyMessage.leftCornerNotificationError(structure.errors[0].message);
            return;
          }

          const extractedFees = [];
          const seenCodes = new Set();
          const seenNames = new Set();

          const addExtractedFee = (f) => {
            const code = String(f.revenueCode || "").trim().toUpperCase();
            const name = String(f.name || f.revenueName || "").trim().toLowerCase();
            if (!name || !code || !String(f.idx || "").trim() || ["—", "-"].includes(String(f.idx).trim())) {
              throw new Error("Danh mục phải có đủ mã khoản, tên khoản và mã định danh. Vui lòng tải lại file mẫu mới.");
            }
            if (code && seenCodes.has(code)) return;
            if (seenNames.has(name)) return;

            if (code) seenCodes.add(code);
            seenNames.add(name);

            extractedFees.push({
              name: f.name || f.revenueName,
              revenueName: f.revenueName || f.name,
              revenueCode: f.revenueCode,
              idx: f.idx,
              unit: f.unit || "Tháng",
              quantity: f.quantity !== undefined && f.quantity !== null && f.quantity !== "" ? Number(f.quantity) : 1,
              price: Number(f.price || 0),
              discountNumber: Number(f.discountNumber || 0),
              source: "Từ file mẫu",
              isCustom: true,
              isFromTemplateCatalog: true,
            });
          };

          // V2 chỉ lấy danh mục từ Sheet "Khoản Thu"
          const feeSheetName = workbookInfo.feeName;

          if (feeSheetName) {
            const feeData = workbookInfo.feeRows;

            let feeHeaderRowIdx = 2;
            for (let i = 0; i < Math.min(6, feeData.length); i++) {
              const r = feeData[i] || [];
              if (r.some((cell) => String(cell || "").toLowerCase().includes("mã khoản"))) {
                feeHeaderRowIdx = i;
                break;
              }
            }

            for (let i = feeHeaderRowIdx + 1; i < feeData.length; i++) {
              const r = feeData[i];
              if (!r || r.length === 0) continue;
              const revCode = r[1] ? String(r[1]).trim() : "";
              const identifierIdx = r[2] ? String(r[2]).trim() : "";
              const revName = r[3] ? String(r[3]).trim() : "";
              const unitVal = r[4] ? String(r[4]).trim() : "Tháng";
              const priceVal = Number(r[5] || 0);

              if (revName) {
                addExtractedFee({
                  revenueCode: revCode,
                  idx: identifierIdx,
                  name: revName,
                  revenueName: revName,
                  unit: unitVal,
                  price: priceVal,
                });
              }
            }
          }

          if (extractedFees.length === 0) {
            NotifyMessage.leftCornerNotificationWarning("Không tìm thấy danh mục khoản thu hợp lệ trong file Excel này!");
            return;
          }

          this.uploadedTemplateCatalogFile = file;
          this.importedTemplateFees = extractedFees;
          this.useTemplateCatalogFees = true;
          this.recomputeConfiguredFees();

          NotifyMessage.leftCornerNotificationSuccess(
            `Đã nạp thành công ${extractedFees.length} khoản thu từ file "${file.name}".`
          );
        } catch (err) {
          console.error("Lỗi khi đọc file mẫu:", err);
          NotifyMessage.leftCornerNotificationError(err.message || "Không thể đọc file Excel này. Vui lòng kiểm tra lại định dạng!");
        } finally {
          if (this.$refs.templateCatalogFileInput) {
            this.$refs.templateCatalogFileInput.value = "";
          }
        }
      };

      reader.readAsArrayBuffer(file);
    },

    removeTemplateCatalogFile() {
      this.uploadedTemplateCatalogFile = null;
      this.importedTemplateFees = [];
      this.useTemplateCatalogFees = false;
      this.isTemplateCatalogDragOver = false;
      if (this.$refs.templateCatalogFileInput) {
        this.$refs.templateCatalogFileInput.value = "";
      }
      this.recomputeConfiguredFees();
      NotifyMessage.leftCornerNotificationInfo("Đã gỡ file mẫu. Danh mục khoản thu đã được làm mới.");
    },

    /**
     * Ghép nối dữ liệu từ API 2 và cấu hình hiện tại để tạo Dữ liệu mẫu chuẩn (Benchmark Data)
     */
    buildBenchmarkData(templateData) {
      const students = Array.isArray(templateData.students) ? templateData.students : [];

      // finalFeeColumns lấy trực tiếp danh sách đã gộp và người dùng thấy trên UI
      const finalFeeColumns = [...this.configuredFeeColumns];

      const systemFees = finalFeeColumns.filter((f) => !f.isCustom);
      const customFees = finalFeeColumns.filter((f) => f.isCustom);

      const systemFeeMap = new Map();
      systemFees.forEach((f) => {
        const code = String(f.revenueCode || "").trim().toUpperCase();
        if (code) {
          systemFeeMap.set(code, f);
        }
      });

      const customFeeMap = new Map();
      customFees.forEach((f) => {
        const code = String(f.revenueCode || "").trim().toUpperCase();
        if (code) {
          customFeeMap.set(code, f);
        }
      });

      // Map học sinh theo mã học sinh (chuẩn hóa uppercase)
      const studentMap = new Map();
      const invoiceCodeMap = new Map();
      students.forEach((s) => {
        const code = String(s.studentCode || s.kidCode || "").trim().toUpperCase();
        if (code) {
          studentMap.set(code, s);
        }
        const invCode = String(s.invoiceCode || "").trim().toUpperCase();
        if (invCode && invCode !== "-") {
          invoiceCodeMap.set(invCode, s);
        }
      });

      // Map toàn bộ khoản thu theo mã khoản thu (revenueCode)
      const feeMap = new Map();
      finalFeeColumns.forEach((f) => {
        const code = String(f.revenueCode || "").trim().toUpperCase();
        if (code) {
          feeMap.set(code, f);
        }
      });

      return {
        rawTemplateData: templateData,
        students,
        systemFees,
        systemFeeMap,
        customFees,
        customFeeMap,
        feeColumns: finalFeeColumns,
        studentMap,
        invoiceCodeMap,
        feeMap,
      };
    },

    /**
     * Gọi API 2 lấy dữ liệu mẫu chuẩn của hệ thống kết hợp cấu hình hiện tại
     */
    async fetchBenchmarkData() {
      clearTimeout(this.feeCatalogRefreshTimer);
      const requestSeq = ++this.feeCatalogRequestSeq;
      this.step1TemplateDataReady = false;
      this.step1TemplateDataLoading = false;
      const targetType = this.step1Filter.targetType;
      const targetId = targetType === "CLASS" ? this.step1Filter.idClass : this.step1Filter.idGroup;
      if (!targetId) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn phạm vi Lớp hoặc Nhóm!");
        return null;
      }

      const [month, year] = String(this.step1Filter.monthAndYear).split("-");
      const listIdKids = Array.from(new Set(this.selectedStudents.map((s) => s.idStudent ?? s.id).filter(Boolean)));
      const seenKids = new Set();
      const seenCodes = new Set();
      const listInvoiceCodes = [];

      for (const s of this.selectedStudents) {
        const idKid = s.idStudent ?? s.id;
        const code = String(s.invoiceCode || "").trim();
        if (idKid && code && code !== "-" && !seenKids.has(idKid) && !seenCodes.has(code)) {
          seenKids.add(idKid);
          seenCodes.add(code);
          listInvoiceCodes.push({ idKid, invoiceCode: code });
        }
      }

      const payload = {
        targetType,
        targetId: Number(targetId),
        month: Number(month),
        year: Number(year),
        turn: Number(this.step1Filter.turn || 1),
        listIdKids,
        listInvoiceCodes,
        includeAttendance: Boolean(this.step1Options.includeAttendance),
        includePreviousDebt: Boolean(this.step1Options.includePreviousDebt),
      };

      if (this.step1Options.includeAttendance) {
        payload.attendanceMonth = String(this.step1Options.attendanceMonth);
      }

      this.step1TemplateDataLoading = true;
      try {
        const res = await ManagingStudentInvoiceService.getExcelImportStep1TemplateData(payload);
        if (requestSeq !== this.feeCatalogRequestSeq) return null;
        const templateData = res?.data?.data;
        if (!templateData) {
          throw new Error(res?.data?.message || "Không thể lấy dữ liệu mẫu chuẩn từ hệ thống.");
        }

        // Cập nhật danh mục khoản hệ thống nếu có từ API 2 và recompute danh mục đã merge
        if (Array.isArray(templateData.feeColumns) && templateData.feeColumns.length > 0) {
          this.systemFeeColumns = templateData.feeColumns.map((col) => ({
            name: col.revenueName || col.name,
            revenueName: col.revenueName || col.name,
            revenueCode: col.revenueCode,
            idx: col.idx,
            unit: col.unit || "Tháng",
            quantity: 1,
            price: Number(col.price || 0),
            source: col.source || "Phiếu thu đã phát hành",
            description: col.description || "",
            isCustom: false,
            isExternal: true,
          }));
          this.recomputeConfiguredFees();
        }

        this.benchmarkData = this.buildBenchmarkData(templateData);
        this.step1TemplateDataReady = true;

        return this.benchmarkData;
      } finally {
        if (requestSeq === this.feeCatalogRequestSeq) {
          this.step1TemplateDataLoading = false;
        }
      }
    },

    /**
     * Tải file Excel mẫu gồm 2 Sheets (sử dụng Dữ liệu mẫu chuẩn từ API 2)
     */
    async handleDownloadTemplate() {
      if (this.selectedStudents.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn ít nhất 1 học sinh để đưa vào file mẫu.");
        return;
      }

      if (this.configuredFeeColumns.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Danh mục chưa có khoản thu nào. Vui lòng bật nguồn khoản thu hoặc thêm khoản mới!");
        return;
      }

      this.downloadingTemplate = true;
      try {
        const benchmark = await this.fetchBenchmarkData();
        if (!benchmark) return;

        const [month, year] = String(this.step1Filter.monthAndYear).split("-");

        await downloadInvoiceTemplateWithItemsAtEnd({
          students: benchmark.students,
          feeColumns: benchmark.feeColumns,
          options: {
            includeAttendance: this.step1Options.includeAttendance,
            attendanceMonth: this.step1Options.attendanceMonth,
            includePreviousDebt: this.step1Options.includePreviousDebt,
          },
          context: {
            schoolName: "OneKids School",
            className: this.scopeName || this.currentClassName,
            month: Number(month),
            year: Number(year),
            turn: Number(this.step1Filter.turn || 1),
          },
        });

        NotifyMessage.leftCornerNotificationSuccess("Tải file mẫu Excel (2 Sheets) thành công!");
      } catch (err) {
        console.error("Lỗi gọi API 2 dữ liệu mẫu Excel:", err);
        let msg = err?.response?.data?.message || err?.message || "Có lỗi xảy ra khi tạo file mẫu Excel.";
        if (typeof msg === "object") {
          try {
            msg = msg.message || JSON.stringify(msg);
          } catch (e) {
            msg = "Có lỗi xảy ra khi tạo file mẫu Excel.";
          }
        }
        if (typeof msg === "string") {
          const trimmed = msg.trim();
          if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            try {
              const parsed = JSON.parse(trimmed);
              if (parsed.message) msg = parsed.message;
            } catch (e) {
              // ignore
            }
          }
        }
        NotifyMessage.leftCornerNotificationError(msg);
      } finally {
        this.downloadingTemplate = false;
      }
    },

    /**
     * Chuyển từ Bước 1 sang Bước 2:
     * Tự động gọi API 2 để lấy dữ liệu mẫu chuẩn và ghép các khoản thu tự thêm mới
     */
    async handleNextStep1() {
      if (!this.step1TemplateDataReady || this.loadingBenchmark) return;

      if (this.selectedStudents.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn học sinh trước khi tiếp tục.");
        return;
      }

      this.loadingBenchmark = true;
      try {
        const benchmark = await this.fetchBenchmarkData();
        if (!benchmark) return;

        // Đảm bảo khoảng thời gian hiệu lực đã được thiết lập theo tháng đợt thu
        if (!this.invoicePeriod.dateRange || this.invoicePeriod.dateRange.length < 2) {
          const [m, y] = String(this.step1Filter.monthAndYear || "").split("-");
          const feeMoment = moment(`${y || moment().year()}-${String(m || moment().month() + 1).padStart(2, "0")}-01`, "YYYY-MM-DD");
          this.invoicePeriod.dateRange = [
            feeMoment.clone().startOf("month").format("YYYY-MM-DD"),
            feeMoment.clone().endOf("month").format("YYYY-MM-DD"),
          ];
        }

        this.activeStep = 1;
        NotifyMessage.leftCornerNotificationSuccess("Đã tải dữ liệu mẫu chuẩn từ hệ thống để chuẩn bị đối soát.");
      } catch (err) {
        console.error("Lỗi khi tải dữ liệu chuẩn đối soát từ API 2:", err);
        let msg = err?.response?.data?.message || err?.message || "Có lỗi xảy ra khi lấy dữ liệu chuẩn từ hệ thống.";
        if (typeof msg === "object") {
          try {
            msg = msg.message || JSON.stringify(msg);
          } catch (e) {
            msg = "Có lỗi xảy ra khi lấy dữ liệu chuẩn từ hệ thống.";
          }
        }
        NotifyMessage.leftCornerNotificationError(msg);
      } finally {
        this.loadingBenchmark = false;
      }
    },

    handlePrevStep() {
      if (this.activeStep > 0) {
        this.activeStep--;
      }
    },

    /**
     * Trích xuất thông tin Tháng, Năm và Đợt thu từ dòng 2 của Sheet 1
     * @param {Array} row2 Mảng các ô tại dòng 2
     */
    extractPeriodFromRow2(row2) {
      if (!row2 || !Array.isArray(row2) || row2.length === 0) {
        return { month: null, year: null, turn: null, rawText: "", hasPeriodInfo: false };
      }

      const rawText = row2
        .map((cell) => String(cell !== undefined && cell !== null ? cell : "").trim())
        .filter(Boolean)
        .join(" ");

      let month = null;
      let year = null;
      let turn = null;

      // 1. Tìm tháng và năm dạng MM/YYYY, MM-YYYY, M/YYYY, M-YYYY hoặc "Tháng: 09/2026"
      const myRegex = /(?:tháng\s*[:\s-]*)?(\d{1,2})[/-](\d{4})/i;
      const myMatch = rawText.match(myRegex);
      if (myMatch) {
        const m = Number(myMatch[1]);
        const y = Number(myMatch[2]);
        if (m >= 1 && m <= 12 && y >= 2000 && y <= 2100) {
          month = m;
          year = y;
        }
      }

      // 2. Tìm đợt dạng "Đợt: 1", "Đợt 1", "turn: 1", "turn 1"
      const turnRegex = /(?:đợt|turn)\s*[:\s-]*(\d+)/i;
      const turnMatch = rawText.match(turnRegex);
      if (turnMatch) {
        turn = Number(turnMatch[1]);
      } else {
        // Tìm ô có chữ "đợt" và ô kế tiếp là số
        for (let i = 0; i < row2.length; i++) {
          const val = String(row2[i] !== undefined && row2[i] !== null ? row2[i] : "").trim().toLowerCase();
          if (val === "đợt" || val === "đợt:" || val === "turn" || val === "turn:") {
            const nextVal = row2[i + 1];
            if (nextVal !== undefined && nextVal !== null && !isNaN(Number(nextVal))) {
              turn = Number(nextVal);
              break;
            }
          }
        }
      }

      return {
        month,
        year,
        turn,
        rawText,
        hasPeriodInfo: Boolean(month && year && turn !== null),
      };
    },

    async handleStep2TurnChange(val) {
      this.invoicePeriod.turn = val;
      this.step1Filter.turn = val;

      const [m, y] = String(this.invoicePeriod.monthAndYear || this.step1Filter.monthAndYear || "").split("-");
      if (m && y) {
        this.invoicePeriod.description = `Phiếu thu học phí tháng ${String(m).padStart(2, "0")}.${y} - Đợt ${val}`;
      }

      // Re-validate lại file nếu đã có file upload
      if (this.rawMatrix && this.rawMatrix.length > 0) {
        this.parseAndValidateExcel(this.rawMatrix, this.rawSheet2Rows, this.rawWorkbookMetadata);
        if (this.errorList.length === 0 && this.parsedRows.length > 0) {
          await this.validateOnlinePaymentWithBE();
        }
      }
    },

    // --- XỬ LÝ BƯỚC 2: UPLOAD & VALIDATION ENGINE ---
    handleFileDrop(e) {
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileUpload(e) {
      const files = e.target.files;
      if (files && files.length > 0) {
        this.processFile(files[0]);
      }
    },

    clearUploadedFile() {
      this.uploadedFile = null;
      this.parsedRows = [];
      this.detectedFeeColumns = [];
      this.hasFeeSheet = false;
      this.feeCatalogMap.clear();
      this.hasValidated = false;
      this.errorList = [];
      this.checkingOnlinePayment = false;
      this.onlinePaymentValidated = false;
      this.rawMatrix = null;
      this.rawSheet2Rows = [];
      this.rawWorkbookMetadata = null;
      this.resolvedExcelV2Catalog = new Map();
      if (this.$refs.excelFileInput) {
        this.$refs.excelFileInput.value = "";
      }
    },

    /**
     * Đọc và tiền xử lý file Excel (Đọc cả Sheet 1 và Sheet 2)
     */
    processFile(file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "xlsx") {
        NotifyMessage.leftCornerNotificationError("Vui lòng chỉ tải lên file mẫu Excel (.xlsx)!");
        return;
      }

      this.uploadedFile = file;
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          // Đảm bảo dữ liệu mẫu chuẩn luôn sẵn sàng
          if (!this.benchmarkData) {
            await this.fetchBenchmarkData();
          }

          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const workbookInfo = readInvoiceExcelWorkbook(workbook, (sheet) => XLSX.utils.sheet_to_json(sheet, { header: 1 }));

          // 1. Đọc Sheet 2: "Khoản Thu" nếu có để đối soát Tiêu chí 4
          this.feeCatalogMap.clear();
          this.hasFeeSheet = false;
          const sheet2Rows = [];

          const feeSheetName = workbookInfo.feeName;

          if (feeSheetName) {
            this.hasFeeSheet = true;
            const feeData = workbookInfo.feeRows;

            // Quét tìm dòng header của Sheet 2 (chứa "mã khoản thu" hoặc "mã khoản")
            let feeHeaderRowIdx = 2;
            for (let i = 0; i < Math.min(6, feeData.length); i++) {
              const r = feeData[i] || [];
              if (r.some((cell) => String(cell || "").toLowerCase().includes("mã khoản"))) {
                feeHeaderRowIdx = i;
                break;
              }
            }

            // Dữ liệu Sheet 2 bắt đầu từ dòng kế tiếp header
            for (let i = feeHeaderRowIdx + 1; i < feeData.length; i++) {
              const r = feeData[i];
              if (!r || r.length === 0) continue;
              if (r[1] || r[2] || r[3] || (workbookInfo.metadata.versionMarker === "2" && r.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== ""))) {
                const revCode = r[1] ? String(r[1]).trim() : "";
                const identifierIdx = r[2] ? String(r[2]).trim() : "";
                const revName = r[3] ? String(r[3]).trim() : "";
                const unitVal = r[4] ? String(r[4]).trim() : "Tháng";
                const priceVal = Number(r[5] || 0);
                const isCustomVal = r[6]
                  ? (String(r[6]).toLowerCase().includes("tự thêm") || String(r[6]).toLowerCase().includes("tự khởi tạo"))
                  : false;

                sheet2Rows.push({
                  rowNum: i + 1,
                  revenueCode: revCode,
                  idx: identifierIdx,
                  revenueName: revName,
                  unit: unitVal,
                  price: priceVal,
                  isCustom: isCustomVal,
                  source: r[6] ? String(r[6]).trim() : "",
                });

                if (revCode) {
                  this.feeCatalogMap.set(revCode.toUpperCase(), {
                    revenueCode: revCode,
                    idx: identifierIdx,
                    revenueName: revName,
                    unit: unitVal,
                    price: priceVal,
                    isCustom: isCustomVal,
                  });
                }
              }
            }
          }

          // 2. Đọc Sheet 1: "Danh Sách HS"
          const rawMatrix = workbookInfo.matrix;
          this.rawMatrix = rawMatrix;
          this.rawSheet2Rows = sheet2Rows;
          this.rawWorkbookMetadata = workbookInfo.metadata;

          this.parseAndValidateExcel(rawMatrix, sheet2Rows, workbookInfo.metadata);

          // Ý người dùng: Sau khi check FE xong mà KHÔNG CÓ LỖI (errorList = 0) mới gọi API check thanh toán trực tuyến
          if (this.errorList.length === 0 && this.parsedRows.length > 0) {
            await this.validateOnlinePaymentWithBE();
          }
        } catch (err) {
          console.error(err);
          NotifyMessage.leftCornerNotificationError("Không thể đọc file Excel. Vui lòng kiểm tra định dạng file!");
        }
      };

      reader.readAsArrayBuffer(file);
    },

    /**
     * Engine Đối Soát Dữ Liệu Excel Phía Frontend (FE Strict Validation - Step 2)
     * So sánh chặt chẽ với Dữ liệu mẫu chuẩn (Benchmark Data) theo 4 tiêu chí:
     * 1. Mã học sinh có khớp nhau không? (Trống, trùng lặp, thuộc hệ thống, thiếu học sinh chuẩn)
     * 2. Mã học sinh + Họ tên + Số điện thoại chính xác không?
     * 3. Tên lớp + Khối chính xác không?
     * 4. Mã khoản + Mã định danh + Tên khoản chính xác hay không? (So sánh Mã khoản + Tên khoản từ Sheet 1 và Sheet 2)
     */
    parseAndValidateExcel(matrix, sheet2Rows = [], workbookMetadata = null) {
      this.errorList = [];
      this.parsedRows = [];
      this.detectedFeeColumns = [];
      const layout = prepareInvoiceExcelLayout(matrix, sheet2Rows, workbookMetadata, this.benchmarkData || {});
      this.resolvedExcelV2Catalog = layout.catalog;
      if (layout.errors.length) {
        this.errorList = layout.errors;
        this.hasValidated = true;
        this.onlinePaymentValidated = false;
        return;
      }
      matrix = layout.matrix;

      if (!matrix || matrix.length < 5) {
        this.errorList.push({
          row: 1,
          studentCode: "-",
          studentName: "-",
          field: "Cấu trúc file",
          value: "Quá ít dòng",
          expected: "Tối thiểu 5 dòng theo cấu trúc mẫu OneKids",
          message: "File Excel không đúng cấu trúc mẫu chuẩn OneKids (thiếu dòng tiêu đề hoặc dữ liệu học sinh).",
        });
        this.hasValidated = true;
        return;
      }

      // 0. Kiểm tra Tháng & Đợt thu tại Dòng 2 của file Excel
      const row2 = matrix[1] || [];
      const extractedPeriod = this.extractPeriodFromRow2(row2);

      const [curM, curY] = String(
        (this.invoicePeriod && this.invoicePeriod.monthAndYear) ||
        (this.step1Filter && this.step1Filter.monthAndYear) ||
        ""
      ).split("-");
      const expectedMonth = Number(curM);
      const expectedYear = Number(curY);
      const expectedTurn = Number((this.invoicePeriod && this.invoicePeriod.turn) || 1);

      if (!extractedPeriod.hasPeriodInfo) {
        this.errorList.push({
          row: 2,
          studentCode: "-",
          studentName: "-",
          field: "Tháng / Đợt thu (Dòng 2)",
          value: extractedPeriod.rawText || "Trống",
          expected: `Tháng: ${String(expectedMonth).padStart(2, "0")}/${expectedYear} - Đợt: ${expectedTurn}`,
          message: "Dòng 2 của file Excel không chứa thông tin Tháng và Đợt thu hợp lệ theo mẫu OneKids.",
        });
      } else {
        if (extractedPeriod.month !== expectedMonth || extractedPeriod.year !== expectedYear) {
          const fileMonthStr = extractedPeriod.month && extractedPeriod.year
            ? `${String(extractedPeriod.month).padStart(2, "0")}/${extractedPeriod.year}`
            : "Không xác định";
          const expectedMonthStr = `${String(expectedMonth).padStart(2, "0")}/${expectedYear}`;
          this.errorList.push({
            row: 2,
            studentCode: "-",
            studentName: "-",
            field: "Tháng thu (Dòng 2)",
            value: `Tháng ${fileMonthStr}`,
            expected: `Tháng ${expectedMonthStr}`,
            message: `Tháng thu trong file Excel (${fileMonthStr}) không khớp với tháng thu đang cấu hình (${expectedMonthStr}).`,
          });
        }

        if (extractedPeriod.turn !== expectedTurn) {
          this.errorList.push({
            row: 2,
            studentCode: "-",
            studentName: "-",
            field: "Đợt thu (Dòng 2)",
            value: extractedPeriod.turn !== null ? `Đợt ${extractedPeriod.turn}` : "Chưa có",
            expected: `Đợt ${expectedTurn}`,
            message: `Đợt thu trong file Excel (${extractedPeriod.turn !== null ? 'Đợt ' + extractedPeriod.turn : 'Chưa có'}) không khớp với đợt thu đã chọn tại Bước 2 (Đợt ${expectedTurn}).`,
          });
        }
      }

      // Xác định dòng header trong Sheet 1
      let headerRow2Index = 3; // Mặc định dòng 4 trong Excel (index 3)
      for (let r = 0; r < Math.min(8, matrix.length); r++) {
        const row = matrix[r] || [];
        if (row.some((cell) => String(cell || "").toLowerCase().includes("mã học sinh") || String(cell || "").toLowerCase().includes("ma hoc sinh"))) {
          headerRow2Index = r;
          break;
        }
      }

      const headerRow1Index = headerRow2Index > 0 ? headerRow2Index - 1 : 2;
      const dataStartRowIndex = headerRow2Index + 1; // Dữ liệu từ dòng kế tiếp (thường là Row 5, index 4)

      const rowH1 = matrix[headerRow1Index] || [];
      const rowH2 = matrix[headerRow2Index] || [];

      // Tìm vị trí các cột thông tin học sinh (quét cả 2 dòng header do có ô merge dọc)
      const findColIndex = (keyword, defaultIdx) => {
        const kw = keyword.toLowerCase();
        let found = rowH2.findIndex((c) => String(c || "").toLowerCase().includes(kw));
        if (found < 0) {
          found = rowH1.findIndex((c) => String(c || "").toLowerCase().includes(kw));
        }
        return found >= 0 ? found : defaultIdx;
      };

      const findColIndexStrict = (keyword) => {
        const kw = keyword.toLowerCase();
        let found = rowH2.findIndex((c) => String(c || "").toLowerCase().includes(kw));
        if (found < 0) {
          found = rowH1.findIndex((c) => String(c || "").toLowerCase().includes(kw));
        }
        return found;
      };

      const colKidCode = findColIndex("mã học sinh", 1);
      const colFullName = findColIndex("họ và tên", 2);
      const colPhone = findColIndex("số điện thoại", 4);
      const colNickName = findColIndex("biệt danh", 5);
      const colClass = findColIndex("lớp", 6);
      const colGrade = findColIndex("khối", 7);

      // Tìm cột bắt đầu của khối "Thông tin thanh toán" (Thiếu/ Thừa phiếu thu trước)
      let payColStart = -1;
      for (let c = 17; c < rowH2.length; c++) {
        const h2 = String(rowH2[c] || "").toLowerCase();
        const h1 = String(rowH1[c] || "").toLowerCase();
        if (h2.includes("thiếu/ thừa") || h2.includes("thừa phiếu") || h1.includes("thanh toán") || h2.includes("phải thu")) {
          payColStart = c;
          break;
        }
      }

      // Nhận diện các cột khoản thu dựa trên header “Mã khoản” trong rowH2
      // Ma trận kiểm tra nội bộ đã ghép metadata danh mục: Mã khoản | Tên khoản | Đơn giá | ĐV | Số lượng | Giảm giá | Thành tiền
      const feeGroups = [];
      const maxFeeCol = payColStart > 17 ? payColStart : rowH2.length;

      // Cách chính xác: quét rowH2 tìm các cột có header “Mã khoản”
      for (let c = 17; c < maxFeeCol; c++) {
        const h2Cell = String(rowH2[c] || "").toLowerCase().trim();
        if (h2Cell.includes("mã khoản")) {
          // Lấy tiêu đề khoản thu từ rowH1 (dòng merge phía trên)
          let feeTitle = String(rowH1[c] || "").trim();
          if (!feeTitle) {
            // Tìm lùi trong rowH1 để lấy tiêu đề merge
            for (let k = c; k >= 17; k--) {
              if (rowH1[k] && !String(rowH1[k]).toLowerCase().includes("điểm danh")) {
                feeTitle = String(rowH1[k]).trim();
                break;
              }
            }
          }
          feeTitle = feeTitle || `Khoản thu ${feeGroups.length + 1}`;

          feeGroups.push({
            startIndex: c,
            title: feeTitle,
          });
        }
      }

      this.detectedFeeColumns = feeGroups;

      if (payColStart === -1) {
        payColStart = feeGroups.length > 0
          ? feeGroups[feeGroups.length - 1].startIndex + 7
          : 17;
      }

      // Xác định cột Mã phiếu thu và Ghi chú (nằm sau 6 cột thanh toán)
      const strictInvoiceCol = findColIndexStrict("mã phiếu");
      const colInvoiceCode = strictInvoiceCol >= 0 ? strictInvoiceCol : (payColStart !== -1 ? payColStart + 6 : -1);
      const colNote = findColIndex("ghi chú", colInvoiceCode !== -1 ? colInvoiceCode + 1 : payColStart + 7);

      if (strictInvoiceCol === -1) {
        this.errorList.push({
          row: "Tiêu đề cột (Dòng 3 - 4)",
          studentCode: "—",
          studentName: "—",
          field: "Cột Mã phiếu thu",
          value: "(Thiếu cột)",
          expected: "Mã phiếu thu",
          message: "Sheet 'Danh Sách HS' thiếu cột 'Mã phiếu thu' (nằm trước cột Ghi chú). Vui lòng sử dụng file mẫu mới nhất có cột 'Mã phiếu thu' để đảm bảo tính toàn vẹn dữ liệu.",
        });
      }

      const systemFeeMap = this.benchmarkData?.systemFeeMap || new Map();
      const systemFees = this.benchmarkData?.systemFees || [];
      const customFeeMap = this.benchmarkData?.customFeeMap || new Map();
      const benchmarkStudentMap = this.benchmarkData?.studentMap || new Map();
      const benchmarkInvoiceCodeMap = this.benchmarkData?.invoiceCodeMap || new Map();
      const benchmarkStudents = this.benchmarkData?.students || [];

      // Map danh mục từ Sheet 2 ("Khoản Thu")
      const sheet2FeeMap = new Map();
      sheet2Rows.forEach((r) => {
        const code = String(r.revenueCode || "").trim().toUpperCase();
        if (code) {
          sheet2FeeMap.set(code, r);
        }
      });

      // =========================================================================
      // TIÊU CHÍ 4: ĐỐI SOÁT CÁC KHOẢN THU TRÊN FILE EXCEL THEO 2 NGUYÊN TẮC:
      // 1. Khoản có nguồn gốc từ hệ thống: Giữ nguyên đối soát hiện tại, không được thiếu bất kỳ khoản hệ thống nào.
      // 2. Khoản tự thêm mới: Đối chiếu qua 2 sheet để khớp Mã khoản thu, Mã định danh (idx), Tên khoản thu.
      // =========================================================================

      // 4.1 Quét và đối soát từng cột khoản thu trên Sheet 1
      const detectedFeeCodesOnSheet1 = new Set();
      const detectedFeeTitlesOnSheet1 = [];

      feeGroups.forEach((group) => {
        // Tìm mã khoản và tên khoản xuất hiện trong các dòng dữ liệu của cột này
        let colCode = "";
        // nameFromH1: tên lấy từ header dòng 3 (group.title)
        const nameFromH1 = group.title;
        // nameFromData: tên lấy từ cột "Tên khoản" trong dòng dữ liệu (startIndex + 1)
        let nameFromData = "";

        for (let r = dataStartRowIndex; r < matrix.length; r++) {
          const row = matrix[r];
          if (!row || row.length === 0) continue;
          const rawCode = row[group.startIndex];
          const rawName = row[group.startIndex + 1];
          if (!colCode && rawCode) {
            colCode = String(rawCode).trim();
          }
          if (!nameFromData && rawName) {
            nameFromData = String(rawName).trim();
          }
          // Dừng khi đã có cả mã và tên từ dữ liệu
          if (colCode && nameFromData) break;
        }

        // colName ưu tiên lấy từ dữ liệu dòng, fallback về header
        const colName = nameFromData || nameFromH1;

        if (colCode) {
          group.code = colCode;
          detectedFeeCodesOnSheet1.add(colCode.toUpperCase());
        }
        if (colName) {
          group.title = colName;
        }
        detectedFeeTitlesOnSheet1.push(colName.toLowerCase());

        // Kiểm tra Mã khoản trên Sheet 1
        if (!colCode) {
          this.errorList.push({
            row: `Cột "${group.title}"`,
            studentCode: "Khoản thu",
            studentName: colName || group.title,
            field: "Mã khoản (Sheet 1)",
            value: "(Trống)",
            expected: "Mã khoản thu hợp lệ",
            message: `Cột khoản thu "${group.title}" trên Sheet 1 bị trống Mã khoản thu ở các dòng dữ liệu.`,
          });
          return;
        }

        const upperColCode = colCode.toUpperCase();
        const sysFee = systemFeeMap.get(upperColCode);

        if (sysFee) {
          // =======================================================================
          // NGUYÊN TẮC 1: KHOẢN CÓ NGUỒN GỐC TỪ HỆ THỐNG (Giữ nguyên đối soát hiện tại)
          // Kiểm tra cả tên từ header (rowH1) lẫn tên từ dòng dữ liệu
          // =======================================================================
          const expectedName = (sysFee.revenueName || sysFee.name || "").trim().toLowerCase();

          // Kiểm tra tên từ dòng dữ liệu (nameFromData) - quan trọng nhất vì đây là dữ liệu thực
          if (nameFromData && expectedName && nameFromData.toLowerCase() !== expectedName) {
            this.errorList.push({
              row: `Cột "${nameFromH1}" - Dòng dữ liệu (Sheet 1)`,
              studentCode: colCode,
              studentName: nameFromData,
              field: "Tên khoản hệ thống (Sheet 1 - Dòng dữ liệu)",
              value: nameFromData,
              expected: (sysFee.revenueName || sysFee.name || "").trim(),
              message: `Tên khoản hệ thống tại cột dữ liệu "${nameFromData}" trên Sheet 1 không khớp với tên khoản chuẩn ("${(sysFee.revenueName || sysFee.name || "").trim()}") của mã "${colCode}".`,
            });
          }

          // Kiểm tra tên từ header (nameFromH1/group.title) - tên khoản ở Row 3
          if (nameFromH1 && expectedName && nameFromH1.toLowerCase() !== expectedName) {
            this.errorList.push({
              row: `Cột "${nameFromH1}" - Header Row 3 (Sheet 1)`,
              studentCode: colCode,
              studentName: nameFromH1,
              field: "Tên khoản hệ thống (Sheet 1 - Header)",
              value: nameFromH1,
              expected: (sysFee.revenueName || sysFee.name || "").trim(),
              message: `Tên khoản hệ thống tại tiêu đề cột "${nameFromH1}" (Header Row 3) trên Sheet 1 không khớp với tên khoản chuẩn ("${(sysFee.revenueName || sysFee.name || "").trim()}") của mã "${colCode}".`,
            });
          }
        } else {
          // =======================================================================
          // NGUYÊN TẮC 2: KHOẢN TỰ THÊM MỚI (ĐỐI CHIẾU QUA 2 SHEET)
          // =======================================================================
          if (sheet2Rows.length === 0) {
            this.errorList.push({
              row: `Cột "${group.title}" (Sheet 1)`,
              studentCode: colCode,
              studentName: colName || group.title,
              field: "Khoản tự thêm mới",
              value: colCode,
              expected: "Có Sheet 'Khoản Thu' để đối chiếu",
              message: `Khoản thu tự thêm mới "${colName}" (Mã: "${colCode}") trên Sheet 1 thiếu Sheet 'Khoản Thu' để đối chiếu mã khoản, mã định danh và tên khoản.`,
            });
          } else {
            const sheet2Item = sheet2FeeMap.get(upperColCode);
            if (!sheet2Item) {
              this.errorList.push({
                row: `Cột "${group.title}" (Sheet 1)`,
                studentCode: colCode,
                studentName: colName || group.title,
                field: "Khoản tự thêm mới (Sheet 1 vs Sheet 2)",
                value: colCode,
                expected: `Có khai báo ở Sheet 'Khoản Thu'`,
                message: `Khoản thu tự thêm mới "${colName}" (Mã: "${colCode}") trên Sheet 1 chưa được khai báo ở Sheet 'Khoản Thu'. Cần khai báo trên cả 2 sheet để đối chiếu mã khoản, mã định danh và tên khoản.`,
              });
            } else {
              // 1. Đối chiếu Tên khoản thu qua 2 sheet - kiểm tra cả header (rowH1) và dòng dữ liệu
              const s2Name = String(sheet2Item.revenueName || "").trim();
              const s2NameLower = s2Name.toLowerCase();

              // 1a. So sánh tên từ dòng dữ liệu (nameFromData)
              if (nameFromData && s2Name && nameFromData.toLowerCase() !== s2NameLower) {
                this.errorList.push({
                  row: `Cột "${nameFromH1}" - Dòng dữ liệu (Sheet 1 vs Sheet 2)`,
                  studentCode: colCode,
                  studentName: nameFromData,
                  field: "Tên khoản tự thêm mới (Sheet 1 dữ liệu vs Sheet 2)",
                  value: nameFromData,
                  expected: s2Name,
                  message: `Tên khoản tại dòng dữ liệu Sheet 1 ("${nameFromData}") không khớp với tên khoản trên Sheet 'Khoản Thu' ("${s2Name}") của mã "${colCode}".`,
                });
              }

              // 1b. So sánh tên từ header Row 3 (nameFromH1)
              if (nameFromH1 && s2Name && nameFromH1.toLowerCase() !== s2NameLower) {
                this.errorList.push({
                  row: `Cột "${nameFromH1}" - Header Row 3 (Sheet 1 vs Sheet 2)`,
                  studentCode: colCode,
                  studentName: nameFromH1,
                  field: "Tên khoản tự thêm mới (Sheet 1 Header vs Sheet 2)",
                  value: nameFromH1,
                  expected: s2Name,
                  message: `Tiêu đề cột "${nameFromH1}" (Header Row 3) trên Sheet 1 không khớp với tên khoản trên Sheet 'Khoản Thu' ("${s2Name}") của mã "${colCode}".`,
                });
              }

              // 2. Đối chiếu Mã định danh (idx) trên Sheet 2
              const s2Idx = String(sheet2Item.idx || "").trim();
              if (!s2Idx) {
                this.errorList.push({
                  row: `Sheet 'Khoản Thu' Dòng ${sheet2Item.rowNum}`,
                  studentCode: colCode,
                  studentName: s2Name || colName,
                  field: "Mã định danh (idx) tự thêm mới",
                  value: "(Trống)",
                  expected: "Mã định danh hợp lệ",
                  message: `Khoản thu tự thêm mới "${s2Name || colName}" (Mã: "${colCode}") trên Sheet 'Khoản Thu' bị thiếu Mã định danh (idx).`,
                });
              } else {
                const benchCustomFee = customFeeMap.get(upperColCode);
                if (benchCustomFee && benchCustomFee.idx && benchCustomFee.idx !== s2Idx) {
                  this.errorList.push({
                    row: `Sheet 'Khoản Thu' Dòng ${sheet2Item.rowNum}`,
                    studentCode: colCode,
                    studentName: s2Name || colName,
                    field: "Mã định danh (idx) tự thêm mới",
                    value: s2Idx,
                    expected: benchCustomFee.idx,
                    message: `Mã định danh (idx) "${s2Idx}" của khoản tự thêm mới "${s2Name}" trên Sheet 'Khoản Thu' không khớp với mã định danh ban đầu ("${benchCustomFee.idx}").`,
                  });
                }
              }
            }
          }
        }
      });

      // 4.2 NGUYÊN TẮC 1: Không được thiếu bất kỳ khoản hệ thống nào trên Sheet 1
      // Ưu tiên kiểm tra theo Mã khoản (revenueCode) - key chính xác nhất
      systemFees.forEach((sFee) => {
        const sCode = (sFee.revenueCode || "").trim().toUpperCase();
        const sName = (sFee.revenueName || sFee.name || "").trim().toLowerCase();

        // Chỉ kiểm tra theo mã khoản (chính xác), không fallback sang tên để tránh bỏ sót lỗi
        const hasCode = sCode && detectedFeeCodesOnSheet1.has(sCode);
        // Chỉ dùng tên làm fallback khi khoản hệ thống không có mã (edge case)
        const hasName = !sCode && sName && detectedFeeTitlesOnSheet1.some((t) => t === sName);

        if (!hasCode && !hasName) {
          this.errorList.push({
            row: "Toàn file (Sheet 1)",
            studentCode: sCode || "—",
            studentName: sFee.revenueName || sFee.name,
            field: "Khoản thu hệ thống bắt buộc",
            value: "Bị thiếu trên Sheet 1",
            expected: `${sFee.revenueName || sFee.name} (Mã: ${sCode})`,
            message: `File Excel (Sheet 1) thiếu cột khoản thu hệ thống bắt buộc "${sFee.revenueName || sFee.name}" (Mã: ${sCode || '—'}). Không được thiếu bất kỳ khoản hệ thống nào.`,
          });
        }
      });

      // 4.3 Đối soát danh mục trên Sheet 2 ("Khoản Thu")
      if (sheet2Rows.length > 0) {
        sheet2Rows.forEach((r) => {
          const rCode = (r.revenueCode || "").trim().toUpperCase();
          const rIdx = (r.idx || "").trim();
          const rName = (r.revenueName || "").trim();

          if (!rCode) {
            this.errorList.push({
              row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
              studentCode: "Khoản thu",
              studentName: rName || "—",
              field: "Mã khoản (Sheet 2)",
              value: "(Trống)",
              expected: "Mã khoản thu hợp lệ",
              message: `Dòng ${r.rowNum} trên Sheet 'Khoản Thu' bị để trống Mã khoản thu.`,
            });
            return;
          }

          if (!rIdx) {
            this.errorList.push({
              row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
              studentCode: rCode,
              studentName: rName || "—",
              field: "Mã định danh (idx) (Sheet 2)",
              value: "(Trống)",
              expected: "Mã định danh hợp lệ",
              message: `Dòng ${r.rowNum} trên Sheet 'Khoản Thu' bị để trống Mã định danh (idx).`,
            });
          }

          if (!rName) {
            this.errorList.push({
              row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
              studentCode: rCode,
              studentName: "—",
              field: "Tên khoản (Sheet 2)",
              value: "(Trống)",
              expected: "Tên khoản thu hợp lệ",
              message: `Dòng ${r.rowNum} trên Sheet 'Khoản Thu' bị để trống Tên khoản thu.`,
            });
          }

          const sysFee = systemFeeMap.get(rCode) || systemFeeMap.get(rCode.toUpperCase());
          if (sysFee) {
            // NGUYÊN TẮC 1: Khoản hệ thống -> Đối soát với dữ liệu chuẩn hệ thống
            const benchIdx = (sysFee.idx || "").trim();
            if (rIdx && benchIdx && rIdx !== benchIdx) {
              this.errorList.push({
                row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
                studentCode: rCode,
                studentName: rName || "—",
                field: "Mã định danh (idx) (Sheet 2)",
                value: rIdx,
                expected: benchIdx,
                message: `Mã định danh (idx) "${rIdx}" ở Sheet 'Khoản Thu' không chính xác với mã định danh chuẩn ("${benchIdx}") của khoản hệ thống "${rCode}".`,
              });
            }

            const benchName = (sysFee.revenueName || sysFee.name || "").trim();
            if (rName && benchName && rName.toLowerCase() !== benchName.toLowerCase()) {
              this.errorList.push({
                row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
                studentCode: rCode,
                studentName: rName,
                field: "Tên khoản (Sheet 2)",
                value: rName,
                expected: benchName,
                message: `Tên khoản "${rName}" ở Sheet 'Khoản Thu' không khớp với tên chuẩn ("${benchName}") của khoản hệ thống "${rCode}".`,
              });
            }
          } else {
            // NGUYÊN TẮC 2: Khoản tự thêm mới -> Đối chiếu với Sheet 1
            // 1. Khoản tự thêm mới trên Sheet 2 phải có cột tương ứng trên Sheet 1
            if (!detectedFeeCodesOnSheet1.has(rCode)) {
              this.errorList.push({
                row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
                studentCode: rCode,
                studentName: rName,
                field: "Khoản tự thêm mới (Sheet 2 vs Sheet 1)",
                value: `Có trên Sheet 'Khoản Thu'`,
                expected: `Có cột tương ứng trên Sheet 1`,
                message: `Khoản thu tự thêm mới "${rName}" (Mã: "${rCode}") được khai báo ở Sheet 'Khoản Thu' nhưng thiếu cột tương ứng trên Sheet 1 ('Danh Sách HS').`,
              });
            }

            // 2. Nếu khoản tự thêm mới có trong cấu hình ban đầu Bước 1, kiểm tra khớp idx và tên
            const benchCustomFee = customFeeMap.get(rCode);
            if (benchCustomFee) {
              if (benchCustomFee.idx && rIdx && rIdx !== benchCustomFee.idx) {
                this.errorList.push({
                  row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
                  studentCode: rCode,
                  studentName: rName,
                  field: "Mã định danh (idx) tự thêm mới (Sheet 2)",
                  value: rIdx,
                  expected: benchCustomFee.idx,
                  message: `Mã định danh (idx) "${rIdx}" của khoản tự thêm mới "${rName}" ở Sheet 'Khoản Thu' không khớp với mã ban đầu ("${benchCustomFee.idx}").`,
                });
              }
              const benchCustomName = (benchCustomFee.name || benchCustomFee.revenueName || "").trim();
              if (benchCustomName && rName && rName.toLowerCase() !== benchCustomName.toLowerCase()) {
                this.errorList.push({
                  row: `Sheet 'Khoản Thu' Dòng ${r.rowNum}`,
                  studentCode: rCode,
                  studentName: rName,
                  field: "Tên khoản tự thêm mới (Sheet 2)",
                  value: rName,
                  expected: benchCustomName,
                  message: `Tên khoản tự thêm mới "${rName}" ở Sheet 'Khoản Thu' không khớp với tên ban đầu ("${benchCustomName}").`,
                });
              }
            }
          }
        });
      }

      // =========================================================================
      // TIÊU CHÍ 1, 2, 3: ĐỐI SOÁT TỪNG DÒNG HỌC SINH TRÊN SHEET 1
      // =========================================================================
      const seenStudentCodes = new Map();
      const seenFileInvoiceCodes = new Map();

      for (let r = dataStartRowIndex; r < matrix.length; r++) {
        const row = matrix[r];
        if (!row || row.length === 0 || row.every((val) => val === null || val === undefined || String(val).trim() === "")) {
          continue;
        }

        const excelRowNum = r + 1; // 1-indexed Excel row
        const studentCodeRaw = row[colKidCode];
        const studentNameRaw = row[colFullName];
        const phoneRaw = row[colPhone];
        const classRaw = row[colClass];
        const gradeRaw = row[colGrade];

        // 1. TIÊU CHÍ 1: Mã học sinh có khớp nhau không?
        // 1.1 Kiểm tra để trống
        if (!studentCodeRaw || String(studentCodeRaw).trim() === "") {
          this.errorList.push({
            row: excelRowNum,
            studentCode: "(Trống)",
            studentName: studentNameRaw || "—",
            field: "Mã học sinh",
            value: "(Trống)",
            expected: "Mã học sinh hợp lệ",
            message: "Mã học sinh không được để trống.",
          });
          continue;
        }

        const studentCode = String(studentCodeRaw).trim().toUpperCase();
        const studentName = String(studentNameRaw || "").trim();
        const studentPhone = String(phoneRaw || "").trim();
        const studentClass = String(classRaw || "").trim();
        const studentGrade = String(gradeRaw || "").trim();

        // 1.2 Kiểm tra trùng lặp học sinh trong file
        if (seenStudentCodes.has(studentCode)) {
          this.errorList.push({
            row: excelRowNum,
            studentCode,
            studentName,
            field: "Mã học sinh (Trùng lặp)",
            value: studentCode,
            expected: "Mỗi học sinh chỉ xuất hiện 1 dòng",
            message: `Mã học sinh "${studentCode}" bị trùng lặp với dòng ${seenStudentCodes.get(studentCode)} trong file Excel.`,
          });
        } else {
          seenStudentCodes.set(studentCode, excelRowNum);
        }

        // 1.3 Đối soát mã học sinh trong hệ thống (dữ liệu chuẩn)
        const benchStudent = benchmarkStudentMap.get(studentCode);
        if (!benchStudent) {
          this.errorList.push({
            row: excelRowNum,
            studentCode,
            studentName,
            field: "Mã học sinh",
            value: studentCode,
            expected: "Tồn tại trong danh sách chuẩn của lớp/nhóm",
            message: `Mã học sinh "${studentCode}" không tồn tại trong danh sách học sinh chuẩn của hệ thống.`,
          });
        } else {
          // 2. TIÊU CHÍ 2: Mã học sinh + Họ tên + Số điện thoại chính xác không?
          // 2.1 So sánh Họ và tên
          const benchName = (benchStudent.fullName || benchStudent.name || "").trim();
          if (studentName && benchName) {
            const normFileName = studentName.toLowerCase().replace(/\s+/g, " ");
            const normBenchName = benchName.toLowerCase().replace(/\s+/g, " ");
            if (normFileName !== normBenchName) {
              this.errorList.push({
                row: excelRowNum,
                studentCode,
                studentName,
                field: "Họ và tên",
                value: studentName,
                expected: benchName,
                message: `Họ tên trên file ("${studentName}") không chính xác với dữ liệu chuẩn của mã "${studentCode}" (Chuẩn: "${benchName}").`,
              });
            }
          }

          // 2.2 So sánh Số điện thoại
          const benchPhone = (benchStudent.phone || benchStudent.parentPhone || "").trim();
          const normFilePhone = studentPhone.replace(/[^0-9]/g, "");
          const normBenchPhone = benchPhone.replace(/[^0-9]/g, "");
          if (benchPhone && normFilePhone !== normBenchPhone) {
            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Số điện thoại",
              value: studentPhone || "(Trống)",
              expected: benchPhone,
              message: `Số điện thoại trên file ("${studentPhone || 'Trống'}") không chính xác với số điện thoại chuẩn của học sinh này (Chuẩn: "${benchPhone}").`,
            });
          }

          // 3. TIÊU CHÍ 3: Tên lớp + Khối chính xác không?
          // 3.1 So sánh Tên lớp
          const benchClass = (benchStudent.className || this.scopeName || this.currentClassName || "").trim();
          if (benchClass && studentClass) {
            if (studentClass.toLowerCase().replace(/\s+/g, " ") !== benchClass.toLowerCase().replace(/\s+/g, " ")) {
              this.errorList.push({
                row: excelRowNum,
                studentCode,
                studentName,
                field: "Tên lớp",
                value: studentClass,
                expected: benchClass,
                message: `Tên lớp trên file ("${studentClass}") không chính xác với lớp chuẩn (Chuẩn: "${benchClass}").`,
              });
            }
          }

          // 3.2 So sánh Khối
          const benchGrade = (benchStudent.gradeName || "").trim();
          if (benchGrade && studentGrade) {
            if (studentGrade.toLowerCase().replace(/\s+/g, " ") !== benchGrade.toLowerCase().replace(/\s+/g, " ")) {
              this.errorList.push({
                row: excelRowNum,
                studentCode,
                studentName,
                field: "Khối",
                value: studentGrade,
                expected: benchGrade,
                message: `Khối trên file ("${studentGrade}") không chính xác với khối chuẩn (Chuẩn: "${benchGrade}").`,
              });
            }
          }
        }

        // Đọc 9 cột chuyên cần (Cột 8 đến 16)
        const arriveT2t6 = Number(row[8] || 0);
        const arriveT7 = Number(row[9] || 0);
        const arriveCn = Number(row[10] || 0);
        const absentCpT2t6 = Number(row[11] || 0);
        const absentKpT2t6 = Number(row[12] || 0);
        const absentCpT7 = Number(row[13] || 0);
        const absentKpT7 = Number(row[14] || 0);
        const leaveLater = Number(row[15] || 0);
        const quantityDate = Number(row[16] || 26);

        // Đọc các khoản thu trên dòng này
        const feeItems = [];

        feeGroups.forEach((colGroup) => {
          const baseIdx = colGroup.startIndex;
          const feeCodeRaw = row[baseIdx]; // 1: Mã khoản
          const feeNameRaw = row[baseIdx + 1]; // 2: Tên khoản
          const feePriceRaw = row[baseIdx + 2]; // 3: Đơn giá
          const feeUnitRaw = row[baseIdx + 3]; // 4: Đơn vị tính
          const feeQtyRaw = row[baseIdx + 4]; // 5: Số lượng
          const feeDiscNumRaw = row[baseIdx + 5]; // 6: Giảm giá
          const feeAmountRaw = row[baseIdx + 6]; // 7: Thành tiền

          let feeCode = feeCodeRaw ? String(feeCodeRaw).trim() : null;
          const feeName = feeNameRaw ? String(feeNameRaw).trim() : colGroup.title;
          const unit = feeUnitRaw ? String(feeUnitRaw).trim() : "Tháng";
          // Yêu cầu: Số lượng khoản thu không bị hạn chế, cho phép số âm hoặc dương tùy ý và bỏ qua việc check số lượng khoản thu có âm hay không ở Bước 2
          const quantity = feeQtyRaw !== undefined && feeQtyRaw !== null && !isNaN(Number(feeQtyRaw)) ? Number(feeQtyRaw) : 0;
          const price = feePriceRaw !== undefined && feePriceRaw !== null && !isNaN(Number(feePriceRaw)) ? Number(feePriceRaw) : 0;
          const discountNumber = feeDiscNumRaw !== undefined && feeDiscNumRaw !== null && !isNaN(Number(feeDiscNumRaw)) ? Number(feeDiscNumRaw) : 0;
          const amount = feeAmountRaw !== undefined && feeAmountRaw !== null && !isNaN(Number(feeAmountRaw)) ? Number(feeAmountRaw) : (price * quantity - discountNumber);

          // Định danh luôn lấy từ danh mục V2 và chi tiết của đúng học sinh.
          const resolvedFee = this.resolvedExcelV2Catalog.get(feeCode ? feeCode.toUpperCase() : "");
          let feeIdx = "";
          try {
            if (!resolvedFee) throw new Error("Không tìm thấy định danh khoản trong danh mục.");
            feeIdx = resolveInvoiceExcelV2DetailIdx(benchStudent, resolvedFee);
          } catch (err) {
            this.errorList.push({ row: excelRowNum, studentCode, studentName, field: "Mã định danh khoản thu", value: feeCode, expected: "Định danh của đúng học sinh", message: err.message });
          }
          const isCustom = resolvedFee ? resolvedFee.isCustom : true;

          feeItems.push({
            revenueCode: feeCode,
            idx: feeIdx,
            revenueName: feeName,
            unit,
            quantity,
            price,
            discountNumber,
            amount,
            isCustom,
          });
        });

        // Đọc các cột thông tin thanh toán (bắt đầu từ payColStart đến payColStart + 5, không còn cột trạng thái)
        const beforeMoney = Number(row[payColStart] || 0);
        const sumMoney = Number(row[payColStart + 1] || 0);
        const collectedMoney = Number(row[payColStart + 2] || 0);
        const cashMoney = Number(row[payColStart + 3] || 0);
        const transferMoney = Number(row[payColStart + 4] || 0);
        // FE đáp ứng quy tắc dự án: afterMoney = collectedMoney - sumMoney + beforeMoney
        const calculatedAfterMoney = collectedMoney - sumMoney + beforeMoney;

        // =========================================================================
        // TIÊU CHÍ 5: ĐỐI SOÁT MÃ PHIẾU THU (Tránh người dùng tự ý sửa, xóa hoặc tạo mã mới)
        // =========================================================================
        let fileInvoiceCode = "";
        if (colInvoiceCode !== -1 && row[colInvoiceCode] !== undefined && row[colInvoiceCode] !== null) {
          fileInvoiceCode = String(row[colInvoiceCode]).trim();
        }
        const rawBenchInvoiceCode = benchStudent ? String(benchStudent.invoiceCode || "").trim() : "";
        const hasBenchInvoice = rawBenchInvoiceCode && rawBenchInvoiceCode !== "-";
        const expectedInvoiceCode = hasBenchInvoice ? rawBenchInvoiceCode : "";

        if (hasBenchInvoice) {
          // TH1: Học sinh ĐÃ CÓ phiếu thu trên hệ thống -> Bắt buộc mã trên file phải khớp chính xác mã chuẩn
          if (!fileInvoiceCode) {
            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Mã phiếu thu",
              value: "(Trống)",
              expected: expectedInvoiceCode,
              message: `Học sinh này đã có phiếu thu trên hệ thống ("${expectedInvoiceCode}"). Bạn không được xóa hoặc để trống mã phiếu thu.`,
            });
          } else if (fileInvoiceCode !== expectedInvoiceCode) {
            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Mã phiếu thu",
              value: fileInvoiceCode,
              expected: expectedInvoiceCode,
              message: `Mã phiếu thu trên file ("${fileInvoiceCode}") không khớp với mã phiếu thu chuẩn trên hệ thống ("${expectedInvoiceCode}"). Bạn không được phép sửa mã phiếu thu.`,
            });
          }
        } else {
          // TH2: Học sinh CHƯA CÓ phiếu thu trên hệ thống -> Cột mã phiếu phải để trống, nghiêm cấm tự tạo mã
          if (fileInvoiceCode && fileInvoiceCode !== "-") {
            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Mã phiếu thu",
              value: fileInvoiceCode,
              expected: "(Để trống)",
              message: `Học sinh này chưa có phiếu thu trong hệ thống, cột "Mã phiếu thu" bắt buộc phải để trống. Nghiêm cấm tự tạo mã phiếu thu để Backend tự xử lý về sau.`,
            });
          }
        }

        // TH3: Kiểm tra nếu mã phiếu thu trên file bị gán nhầm sang học sinh khác
        if (fileInvoiceCode && fileInvoiceCode !== "-") {
          const upperFileCode = fileInvoiceCode.toUpperCase();
          const benchOwner = benchmarkInvoiceCodeMap.get(upperFileCode);
          if (benchOwner) {
            const ownerCode = String(benchOwner.studentCode || benchOwner.kidCode || "").trim().toUpperCase();
            if (ownerCode !== studentCode) {
              this.errorList.push({
                row: excelRowNum,
                studentCode,
                studentName,
                field: "Mã phiếu thu",
                value: fileInvoiceCode,
                expected: expectedInvoiceCode || "(Trống)",
                message: `Mã phiếu thu "${fileInvoiceCode}" trên hệ thống thuộc về học sinh "${benchOwner.fullName}" (Mã HS: ${benchOwner.studentCode}), không được gán cho học sinh "${studentName}" (Mã HS: ${studentCode}).`,
              });
            }
          }

          // TH4: Kiểm tra trùng lặp mã phiếu thu giữa các dòng trong cùng file Excel
          if (seenFileInvoiceCodes.has(upperFileCode)) {
            const prevRowInfo = seenFileInvoiceCodes.get(upperFileCode);
            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Mã phiếu thu",
              value: fileInvoiceCode,
              expected: expectedInvoiceCode || "(Không trùng lặp)",
              message: `Mã phiếu thu "${fileInvoiceCode}" bị trùng với dòng ${prevRowInfo.row} (Học sinh: ${prevRowInfo.studentName}, Mã HS: ${prevRowInfo.studentCode}). Mỗi mã phiếu thu chỉ được sử dụng cho một học sinh duy nhất.`,
            });
          } else {
            seenFileInvoiceCodes.set(upperFileCode, {
              row: excelRowNum,
              studentCode,
              studentName,
            });
          }
        }

        // Đọc cột Ghi chú duy nhất (nằm sau cột Mã phiếu thu)
        const singleNote = row[colNote] !== undefined && row[colNote] !== null
          ? String(row[colNote]).trim()
          : (row[payColStart + 8] ? String(row[payColStart + 8]).trim() : "");

        this.parsedRows.push({
          excelRow: excelRowNum,
          studentCode,
          // Với học sinh chưa có phiếu thu trên hệ thống: NGHIÊM CẤM TỰ TẠO, BẮT BUỘC ĐỂ TRỐNG ("") ĐỂ BE XỬ LÝ VỀ SAU
          invoiceCode: hasBenchInvoice ? (expectedInvoiceCode || fileInvoiceCode || "") : "",
          fullName: studentName || (benchStudent ? benchStudent.fullName : ""),
          nickName: row[colNickName] || (benchStudent ? benchStudent.nickName : ""),
          className: studentClass || this.scopeName || this.currentClassName,
          gradeName: studentGrade || "",
          arriveT2t6,
          arriveT7,
          arriveCn,
          absentCpT2t6,
          absentKpT2t6,
          absentCpT7,
          absentKpT7,
          leaveLater,
          quantityDate,
          beforeMoney,
          sumMoney,
          collectedMoney,
          cashMoney,
          transferMoney,
          afterMoney: calculatedAfterMoney,
          status: "", // BE sẽ tự xét lại trạng thái khi lưu
          note: singleNote,
          feeItems,
          systemStudent: benchStudent,
        });
      }

      // TIÊU CHÍ 1 - Phần 2: Kiểm tra thiếu học sinh so với danh sách mẫu chuẩn
      benchmarkStudents.forEach((bStu) => {
        const bCode = String(bStu.studentCode || bStu.kidCode || "").trim().toUpperCase();
        if (bCode && !seenStudentCodes.has(bCode)) {
          this.errorList.push({
            row: "Toàn file",
            studentCode: bCode,
            studentName: bStu.fullName || bStu.name || "—",
            field: "Mã học sinh (Bị thiếu)",
            value: "Bị thiếu trong file",
            expected: `Phải có học sinh mã ${bCode}`,
            message: `File Excel bị thiếu học sinh "${bStu.fullName || bStu.name}" (Mã: ${bCode}) so với danh sách học sinh mẫu chuẩn của hệ thống.`,
          });
        }
      });

      this.hasValidated = true;
    },

    /**
     * GỌI API KIỂM TRA PHIẾU THU ĐÃ THANH TOÁN TRỰC TUYẾN TỪ BACKEND
     * NGUYÊN TẮC: CHỈ GỌI KHI ĐỐI SOÁT FE HOÀN TOÀN KHÔNG CÓ LỖI (errorList.length === 0)!
     */
    async validateOnlinePaymentWithBE() {
      // 1. Phải tải file và FE đã đối soát xong
      if (!this.hasValidated || !this.uploadedFile || this.parsedRows.length === 0) {
        return false;
      }

      // 2. Nếu FE vẫn còn lỗi đối soát -> Dừng lại ngay, KHÔNG gọi API Backend
      const feErrors = this.errorList.filter((e) => !e.isOnlinePaymentBlocked);
      if (feErrors.length > 0) {
        return false;
      }

      // Xóa các lỗi kiểm tra online trước đó nếu có để kiểm tra lại
      this.errorList = this.errorList.filter((e) => !e.isOnlinePaymentBlocked);

      // Thu thập danh sách các mã phiếu thu duy nhất trên file (đã loại bỏ rỗng và "-")
      const listInvoiceCodes = [...new Set(
        this.parsedRows
          .map((row) => String(row.invoiceCode || "").trim())
          .filter((code) => code && code !== "-")
      )];

      // Nếu không có mã phiếu thu nào (toàn bộ học sinh chưa có phiếu thu) -> hợp lệ luôn
      if (listInvoiceCodes.length === 0) {
        this.onlinePaymentValidated = true;
        return true;
      }

      this.checkingOnlinePayment = true;
      try {
        const monthParts = String(this.invoicePeriod.monthAndYear || this.step1Filter.monthAndYear).split("-");
        const month = Number(monthParts[0]);
        const year = Number(monthParts[1]);
        const turn = Number(this.invoicePeriod.turn || this.step1Filter.turn || 1);
        const targetType = this.step1Filter.targetType;
        const targetId = Number(targetType === "CLASS" ? this.step1Filter.idClass : this.step1Filter.idGroup);

        const payload = {
          listInvoiceCodes,
          targetType,
          targetId,
          month,
          year,
          turn,
        };

        const res = await ManagingStudentInvoiceService.checkExcelImportOnlinePayment(payload);
        const result = res?.data?.data;

        if (!result || result.isValid !== true) {
          const blocked = Array.isArray(result?.blockedInvoices) ? result.blockedInvoices : [];

          // Map từng mã phiếu thu bị chặn vào dòng Excel tương ứng
          const invoiceCodeToRowMap = new Map();
          this.parsedRows.forEach((row) => {
            const code = String(row.invoiceCode || "").trim().toUpperCase();
            if (code) {
              invoiceCodeToRowMap.set(code, row);
            }
          });

          blocked.forEach((item) => {
            const codeUpper = String(item.invoiceCode || "").trim().toUpperCase();
            const matchedRow = invoiceCodeToRowMap.get(codeUpper);
            const excelRowNum = matchedRow ? matchedRow.excelRow : "Toàn file";
            const studentCode = item.studentCode || (matchedRow ? matchedRow.studentCode : "—");
            const studentName = item.fullName || (matchedRow ? matchedRow.fullName : "—");

            const errorMsg = item.message
              || `Phiếu thu "${item.invoiceCode}" đã có trạng thái thanh toán online thành công (onlinePaymentStatus=true); không được phép ghi đè qua Excel.`;

            this.errorList.push({
              row: excelRowNum,
              studentCode,
              studentName,
              field: "Mã phiếu thu",
              value: item.invoiceCode,
              expected: "Chưa thanh toán online",
              message: errorMsg,
              isOnlinePaymentBlocked: true,
            });
          });

          this.onlinePaymentValidated = false;
          const count = blocked.length || result?.blockedCount || 1;
          NotifyMessage.leftCornerNotificationError(
            `Phát hiện ${count} phiếu thu đã được đánh dấu thanh toán online thành công! Vui lòng gỡ hoặc sửa lại file Excel trước khi tiếp tục.`
          );
          return false;
        }

        // Hợp lệ hoàn toàn
        this.onlinePaymentValidated = true;
        NotifyMessage.leftCornerNotificationSuccess("File Excel đối soát hợp lệ và không có phiếu thu nào bị khóa thanh toán trực tuyến!");
        return true;
      } catch (err) {
        console.error("Lỗi khi kiểm tra trạng thái thanh toán trực tuyến:", err);
        let msg = err?.response?.data?.message || err?.message || "Có lỗi xảy ra khi kiểm tra trạng thái thanh toán online.";
        if (typeof msg === "object") {
          try {
            msg = msg.message || JSON.stringify(msg);
          } catch (e) {
            msg = "Có lỗi xảy ra khi kiểm tra trạng thái thanh toán online.";
          }
        }
        this.onlinePaymentValidated = false;
        NotifyMessage.leftCornerNotificationError(msg);
        return false;
      } finally {
        this.checkingOnlinePayment = false;
      }
    },

    async handleNextStep2() {
      if (!this.invoicePeriod.dateRange || this.invoicePeriod.dateRange.length < 2) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn khoảng thời gian hiệu lực!");
        return;
      }
      if (!this.invoicePeriod.description || !this.invoicePeriod.description.trim()) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng nhập mô tả đợt thu!");
        return;
      }

      // Phải tải file và FE đã đối soát xong
      if (!this.hasValidated || !this.uploadedFile || this.parsedRows.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng tải file Excel lên và chờ đối soát hoàn tất!");
        return;
      }

      // Nếu còn bất kỳ lỗi nào trong errorList -> Không cho tiếp tục
      if (this.errorList.length > 0) {
        NotifyMessage.leftCornerNotificationWarning(
          `File Excel còn ${this.errorList.length} lỗi chưa được khắc phục. Vui lòng kiểm tra lại bảng lỗi!`
        );
        return;
      }

      // Nếu chưa kiểm tra online payment với BE (hoặc lần kiểm tra trước lỗi) -> Gọi kiểm tra lại
      if (!this.onlinePaymentValidated) {
        const ok = await this.validateOnlinePaymentWithBE();
        if (!ok) return;
      }

      // Đồng bộ đợt thu từ Bước 2 sang filter
      this.step1Filter.turn = Number(this.invoicePeriod.turn || 1);

      // Hợp lệ hoàn toàn -> Chuyển sang Bước 3
      if (this.generateDbInvoicesOnFE() === false) return;
      this.activeStep = 2;
      this.$nextTick(() => {
        if (this.$refs.previewTable) {
          this.$refs.previewTable.doLayout();
        }
      });
    },

    /**
     * TẠO DỮ LIỆU DB HOÀN CHỈNH TRÊN FRONTEND (B3 - FE DB SIMULATION)
     * - 100% khớp schema thực tế
     * - 1 ghi chú duy nhất (note)
     * - Giữ nguyên revenueCode và idx để BE đối chiếu với khoản trên phiếu thu cũ
     */
    generateDbInvoicesOnFE() {
      if (!hasCompleteInvoiceExcelV2Details(this.parsedRows, "feeItems")) {
        NotifyMessage.leftCornerNotificationError("Dữ liệu thiếu định danh khoản thu. Vui lòng tải lại và đối soát file Excel.");
        return false;
      }
      const monthParts = String(this.invoicePeriod.monthAndYear).split("-");
      const formMonth = Number(monthParts[0]);
      const formYear = Number(monthParts[1]);
      const formTurn = Number((this.invoicePeriod && this.invoicePeriod.turn) || (this.step1Filter && this.step1Filter.turn) || 1);
      const formDateRange = this.invoicePeriod.dateRange;
      const formDescription = this.invoicePeriod.description.trim();

      const targetType = this.step1Filter.targetType;
      const targetId = targetType === "CLASS" ? this.step1Filter.idClass : this.step1Filter.idGroup;

      this.generatedDbInvoices = this.parsedRows.map((row, index) => {
        const student = row.systemStudent || {};
        const studentCode = row.studentCode;
        // Với học sinh chưa có mã phiếu thu từ trước: NGHIÊM CẤM TỰ TẠO, BẮT BUỘC ĐỂ TRỐNG ("") ĐỂ BE XỬ LÝ VỀ SAU
        const invoiceCode = row.invoiceCode ? String(row.invoiceCode).trim() : "";

        // Giữ nguyên mã khoản và idx; cắt ngắn sẽ khiến BE không tìm đúng khoản trên phiếu cũ.
        const invoiceDetails = row.feeItems.map((fee) => {
          const revCode = String(fee.revenueCode || "").trim();
          const feeIdx = String(fee.idx || "").trim();

          return {
            revenueCode: revCode, // Mã khoản thu (revenueCode)
            idx: feeIdx, // Mã định danh hệ thống (idx)
            revenueName: fee.revenueName,
            unit: fee.unit,
            price: fee.price,
            quantity: fee.quantity,
            discountNumber: fee.discountNumber,
            amount: fee.amount,
            isCustom: Boolean(fee.isCustom), // false: Khoản hệ thống, true: Khoản tự khởi tạo
          };
        });

        return {
          id: `fe_import_${Date.now()}_${index}`,
          idx: generateRandomString(28),
          idSchool: student.idSchool || 1,
          idStudent: student.idStudent || student.idKid || student.id || 0,
          fullName: row.fullName,
          nickName: row.nickName || "",
          studentCode,
          className: row.className || this.scopeName || this.currentClassName,
          parentPhone: student.parentPhone || student.phone || "",
          turn: formTurn,
          description: formDescription,
          invoiceCode,
          targetType,
          targetId: targetId || student.targetId || 0,
          targetName: this.scopeName || this.currentClassName,
          month: formMonth,
          year: formYear,
          startValidityPeriod: formDateRange ? formDateRange[0] : "",
          endValidityPeriod: formDateRange ? formDateRange[1] : "",
          arriveT2t6: row.arriveT2t6,
          arriveT7: row.arriveT7,
          arriveCn: row.arriveCn,
          absentCpT2t6: row.absentCpT2t6,
          absentKpT2t6: row.absentKpT2t6,
          absentCpT7: row.absentCpT7,
          absentKpT7: row.absentKpT7,
          leaveLater: row.leaveLater,
          quantityDate: row.quantityDate,
          beforeMoney: row.beforeMoney,
          sumMoney: row.sumMoney,
          collectedMoney: row.collectedMoney,
          cashMoney: row.cashMoney,
          transferMoney: row.transferMoney,
          afterMoney: row.afterMoney,
          status: "", // BE sẽ tự xét lại trạng thái khi lưu
          delActive: true,
          hideReceipt: false,
          parentUnread: false,
          note: row.note || "", // 1 GHI CHÚ DUY NHẤT
          feeItems: row.feeItems || [],
          invoiceDetails,
        };
      });
      return true;
    },

    /**
     * Gọi API Backend lưu danh sách phiếu thu import từ Excel vào CSDL
     */
    async handleCommitInvoicesToBE() {
      console.log("=== Click [Khởi tạo phiếu thu vào hệ thống] ===", {
        invoicesCount: this.generatedDbInvoices ? this.generatedDbInvoices.length : 0,
        activeStep: this.activeStep,
      });

      if (!this.generatedDbInvoices || this.generatedDbInvoices.length === 0) {
        NotifyMessage.leftCornerNotificationWarning("Chưa có phiếu thu nào được khởi tạo!");
        return;
      }
      if (!hasCompleteInvoiceExcelV2Details(this.generatedDbInvoices, "invoiceDetails")) {
        NotifyMessage.leftCornerNotificationError("Dữ liệu thiếu định danh khoản thu; chưa thể gửi tạo phiếu.");
        return;
      }

      const monthParts = String((this.invoicePeriod && this.invoicePeriod.monthAndYear) || (this.step1Filter && this.step1Filter.monthAndYear) || "").split("-");
      const defaultMonth = Number(monthParts[0]) || (moment().month() + 1);
      const defaultYear = Number(monthParts[1]) || moment().year();
      const turn = Number((this.invoicePeriod && this.invoicePeriod.turn) || (this.step1Filter && this.step1Filter.turn) || 1);
      const displayMonth = defaultYear ? `${defaultMonth}/${defaultYear}` : defaultMonth;

      try {
        await this.$confirm(
          `Bạn có chắc chắn khởi tạo phiếu thu cho tháng ${displayMonth} - đợt ${turn} không?`,
          "Xác nhận",
          {
            confirmButtonText: "Đồng ý",
            cancelButtonText: "Hủy bỏ",
            type: "warning",
          }
        );
      } catch (cancel) {
        return;
      }

      this.savingInvoices = true;
      try {
        const targetType = (this.step1Filter && this.step1Filter.targetType) || "CLASS";
        const targetId = targetType === "GROUP"
          ? Number((this.step1Filter && this.step1Filter.idGroup) || 0)
          : Number((this.step1Filter && this.step1Filter.idClass) || 0);

        const formDates = (this.invoicePeriod && this.invoicePeriod.dateRange) || [];

        const invoicesPayload = (this.generatedDbInvoices || []).filter(Boolean).map((inv, idx) => {
          const m = Number(inv.month) || defaultMonth;
          const y = Number(inv.year) || defaultYear;
          const monthYearStr = `${y}-${String(m).padStart(2, "0")}`;
          const sid = Number(inv.idStudent || inv.studentId || inv.idKid || inv.id || 0);
          const hasCode = inv.invoiceCode && String(inv.invoiceCode).trim() !== "-" && String(inv.invoiceCode).trim() !== "";

          const details = (inv.invoiceDetails || []).filter(Boolean).map((fee) => ({
            idx: String(fee.idx || ""),
            revenueCode: String(fee.revenueCode || ""),
            revenueName: String(fee.revenueName || ""),
            unit: String(fee.unit || "Tháng"),
            price: Number(fee.price || 0),
            quantity: fee.quantity !== undefined && fee.quantity !== null && fee.quantity !== "" ? Number(fee.quantity) : 0,
            discountNumber: Number(fee.discountNumber || 0),
            amount: Number(fee.amount || 0),
            isCustom: Boolean(fee.isCustom),
          }));

          const rowPayload = {
            id: inv.id || `fe_import_${Date.now()}_${idx}`,
            studentId: sid,
            idStudent: sid,
            studentCode: String(inv.studentCode || ""),
            studentName: String(inv.fullName || inv.studentName || ""),
            classId: Number(inv.classId || (this.step1Filter && this.step1Filter.idClass) || 0),
            className: String(inv.className || this.scopeName || ""),
            targetType,
            targetId,
            turn,
            month: m,
            year: y,
            monthYear: monthYearStr,
            invoiceCode: hasCode ? String(inv.invoiceCode).trim() : "",
            startValidityPeriod: inv.startValidityPeriod || formDates[0] || `${y}-${String(m).padStart(2, "0")}-01`,
            endValidityPeriod: inv.endValidityPeriod || formDates[1] || moment(`${y}-${String(m).padStart(2, "0")}-01`).endOf("month").format("YYYY-MM-DD"),
            arriveT2t6: Number(inv.arriveT2t6 || 0),
            arriveT7: Number(inv.arriveT7 || 0),
            arriveCn: Number(inv.arriveCn || 0),
            absentCpT2t6: Number(inv.absentCpT2t6 || 0),
            absentKpT2t6: Number(inv.absentKpT2t6 || 0),
            absentCpT7: Number(inv.absentCpT7 || 0),
            absentKpT7: Number(inv.absentKpT7 || 0),
            leaveLater: Number(inv.leaveLater || 0),
            quantityDate: Number(inv.quantityDate || 26),
            beforeMoney: Number(inv.beforeMoney || 0),
            sumMoney: Number(inv.sumMoney || 0),
            note: String(inv.note || ""),
            description: String(inv.description || (this.invoicePeriod && this.invoicePeriod.description) || ""),
            invoiceDetails: details,
          };

          // Nếu là phiếu cũ có mã thì gửi tiền đã thu/còn lại để BE đối chiếu giữ nguyên payment
          if (hasCode) {
            rowPayload.collectedMoney = Number(inv.collectedMoney || 0);
            rowPayload.cashMoney = Number(inv.cashMoney || 0);
            rowPayload.transferMoney = Number(inv.transferMoney || 0);
            rowPayload.afterMoney = Number(inv.afterMoney || 0);
          }

          return rowPayload;
        });

        const payload = {
          targetType,
          targetId,
          turn,
          invoices: invoicesPayload,
        };

        console.log("=== Payload gửi API create-invoices ===", payload);

        // Gọi qua Service hoặc trực tiếp qua http client nếu service singleton chưa reload
        let res;
        if (ManagingStudentInvoiceService && typeof ManagingStudentInvoiceService.createInvoicesFromExcel === "function") {
          res = await ManagingStudentInvoiceService.createInvoicesFromExcel(payload);
        } else {
          console.warn("createInvoicesFromExcel chưa có trên service singleton, gọi qua http trực tiếp");
          res = await http.post("receipt/v1/invoice/excel-import/create-invoices", payload);
        }

        console.log("=== Response từ BE create-invoices ===", res);
        const data = res && res.data && res.data.data;

        if (data) {
          if (data.failedCount === 0) {
            NotifyMessage.leftCornerNotificationSuccess(
              `Khởi tạo thành công ${data.totalProcessed || invoicesPayload.length} phiếu thu (${data.createdCount || 0} tạo mới, ${data.updatedCount || 0} thay thế)!`
            );
            this.dialogVisible = false;
            this.resetState();
            this.$emit("imported-success", {
              turn,
              month: defaultMonth,
              year: defaultYear,
              targetType,
              targetId,
            });
          } else {
            const failedItems = (data.results || []).filter((r) => !r.success);
            const errMsg = failedItems.map((f) => `${f.studentCode || f.id || "Phiếu"}: ${f.message || "Lỗi xử lý"}`).join("; ");
            NotifyMessage.leftCornerNotificationError(`Có ${data.failedCount} phiếu bị lỗi: ${errMsg}`);
          }
        } else {
          NotifyMessage.leftCornerNotificationSuccess("Khởi tạo phiếu thu từ Excel thành công!");
          this.dialogVisible = false;
          this.resetState();
          this.$emit("imported-success", {
            turn,
            month: defaultMonth,
            year: defaultYear,
            targetType,
            targetId,
          });
        }
      } catch (err) {
        console.error("Lỗi khi lưu phiếu thu:", err);
        const msg = (err && err.response && err.response.data && (err.response.data.message || err.response.data.data)) ||
          (err && err.message) ||
          (err ? String(err) : "Lỗi khi lưu phiếu thu vào hệ thống!");
        NotifyMessage.leftCornerNotificationError(msg);
      } finally {
        this.savingInvoices = false;
      }
    },

    /**
     * Nạp dữ liệu vào bảng tableData ngoài màn hình chính FE (fallback)
     */
    handleCommitInvoicesToFE() {
      this.handleCommitInvoicesToBE();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

.wizard-dialog-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wizard-title-main {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}
.wizard-title-sub {
  font-size: 12.5px;
  color: #64748b;
}
.wizard-steps-wrapper {
  margin-bottom: 20px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.wizard-filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;
}
.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.wizard-options-row {
  display: flex;
  gap: 24px;
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-top: 10px;
}
.option-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.option-check-label {
  font-size: 12px;
  color: #1e3a8a;
}
.fee-cards-container {
  margin-top: 14px;
}
.fee-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
}
.fee-card-box {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
  }

  &--system {
    border-top: 4px solid #2563eb;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      border-color: #93c5fd;
      border-top-color: #2563eb;
    }
  }

  &--custom {
    border-top: 4px solid #f59e0b;
    background: linear-gradient(180deg, #ffffff 0%, #fffbeb 100%);

    &:hover {
      border-color: #fcd34d;
      border-top-color: #f59e0b;
    }
  }
}
.fee-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.fee-card-main-meta {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.fee-card-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;

  &--system {
    background: linear-gradient(135deg, #3b82f6, #1d4ed8);
    color: #ffffff;
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.25);
  }

  &--custom {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #ffffff;
    box-shadow: 0 3px 8px rgba(245, 158, 11, 0.25);
  }
}
.fee-card-heading {
  flex: 1;
  min-width: 0;
}
.fee-card-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 2px;
}
.fee-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  word-break: break-word;
}
.fee-card-badge-wrap {
  flex-shrink: 0;
}
.fee-custom-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.fee-source-pill {
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 20px;
  padding: 0 10px;
  height: 24px;
  line-height: 24px;
}
.fee-delete-btn {
  padding: 6px;
  font-size: 12px;
  transition: transform 0.15s;

  &:hover {
    transform: scale(1.1);
  }
}
.fee-card-info-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  background: #f1f5f9;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}
.fee-info-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.info-block-label {
  font-size: 10.5px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.info-block-value-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  font-weight: 700;
  color: #1e40af;
  word-break: break-all;
  display: flex;
  align-items: center;
}
.info-block-value-unit {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.fee-card-price-hero {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 10px 14px;
}
.price-hero-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #166534;
}
.price-hero-figure {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.price-number {
  font-size: 20px;
  font-weight: 800;
  color: #15803d;
  letter-spacing: -0.5px;
}
.price-currency {
  font-size: 12px;
  font-weight: 700;
  color: #166534;
}
.fee-card-bottom {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.bottom-tag {
  font-size: 12px;
  color: #475569;
  display: flex;
  align-items: center;
}
.fee-cards-empty {
  padding: 24px;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #64748b;
  font-size: 13px;

  i {
    font-size: 28px;
    color: #94a3b8;
    margin-bottom: 6px;
    display: block;
  }

  p {
    margin: 0;
  }
}
.step2-layout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.step2-card {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
}
.step2-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.period-info-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #64748b;
}
.info-tag-label {
  font-weight: 600;
  color: #475569;
}
.wizard-form-row {
  display: flex;
  gap: 14px;
}
.form-control-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.control-label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.upload-dropzone-container {
  margin-top: 4px;
}
.upload-dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  padding: 16px 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.upload-dropzone:hover,
.upload-dropzone.is-dragover {
  border-color: #2563eb;
  background: #eff6ff;
}
.upload-dropzone-icon {
  font-size: 32px;
  color: #3b82f6;
  margin-bottom: 4px;
}
.upload-dropzone-text {
  font-size: 13px;
  color: #334155;
}
.upload-dropzone-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}
.uploaded-file-card {
  margin-top: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #10b981;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
/* --- Dropzone nạp danh mục từ mẫu V2 (Thiết kế mới cho phép kéo thả) --- */
.old-template-dropzone {
  margin: 12px 0 10px 0;
  border: 2px dashed #93c5fd;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
  padding: 14px 18px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: #2563eb;
    background: #dbeafe;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.12);
  }

  &.is-dragover {
    border-color: #1d4ed8;
    background: #bfdbfe;
    border-style: solid;
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.2);
  }
}

.old-template-dropzone-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.old-template-dropzone-left {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.old-template-dropzone-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.old-template-dropzone:hover .old-template-dropzone-icon,
.old-template-dropzone.is-dragover .old-template-dropzone-icon {
  transform: translateY(-2px) scale(1.05);
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
}

.old-template-dropzone-text {
  flex: 1;
  min-width: 0;

  .main-text {
    font-size: 13.5px;
    color: #1e293b;
    font-weight: 500;
    line-height: 1.4;

    strong {
      color: #1e40af;
      font-weight: 700;
    }

    .highlight-browse {
      color: #2563eb;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  .sub-text {
    font-size: 12px;
    color: #64748b;
    margin-top: 3px;
    line-height: 1.35;

    code {
      background: #e2e8f0;
      padding: 1px 5px;
      border-radius: 4px;
      color: #334155;
      font-size: 11px;
      font-family: monospace;
    }
  }
}

.btn-browse-old-template {
  flex-shrink: 0;
  border-radius: 8px !important;
  font-weight: 600 !important;
  border-color: #93c5fd !important;
  color: #1d4ed8 !important;
  background: #ffffff !important;
  padding: 8px 14px !important;

  &:hover {
    background: #2563eb !important;
    color: #ffffff !important;
    border-color: #2563eb !important;
  }
}

/* Card trạng thái file mẫu đã nạp thành công */
.old-template-active-card {
  margin: 12px 0 10px 0;
  border: 1.5px solid #10b981;
  background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.08);
  transition: all 0.25s ease;

  &.is-dragover {
    border-color: #059669;
    border-style: dashed;
    background: #d1fae5;
  }
}

.old-template-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #d1fae5;
}

.old-template-file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.old-template-file-badge {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #dcfce7;
  color: #15803d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.old-template-file-details {
  flex: 1;
  min-width: 0;

  .file-name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-meta {
    font-size: 12px;
    color: #475569;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 6px;

    .file-divider {
      color: #94a3b8;
    }

    .file-fee-count strong {
      color: #15803d;
      font-weight: 700;
    }

    .file-drag-hint {
      color: #64748b;
      font-style: italic;
    }
  }
}

.old-template-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.old-template-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;

  .switch-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #334155;
  }

  .switch-options {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
}

/* --- Thiết kế lại các nút: Thêm khoản thu mới & Tải file mẫu Excel --- */
.btn-redesign-add-fee {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border-color: #059669 !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;

  i {
    font-size: 14px;
    font-weight: bold;
  }

  &:hover, &:focus {
    background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
    border-color: #047857 !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35) !important;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2) !important;
  }
}

.btn-redesign-download-template {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;

  i {
    font-size: 14px;
    font-weight: bold;
  }

  &:hover, &:focus {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    border-color: #1d4ed8 !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2) !important;
  }
}

/* --- Khối nút Tải file mẫu Excel đặt dưới khu vực kéo thả file --- */
.template-download-wrapper {
  margin: 12px 0 14px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.template-download-hint {
  font-size: 12.5px;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

/* --- Thiết kế lại Footer Wizard: Nút Đóng luôn ở bên phải cùng --- */
.wizard-footer-redesign {
  display: flex !important;
  justify-content: flex-end !important;
  align-items: center !important;
  flex-wrap: wrap;
  gap: 10px;
  width: 100% !important;

  .wizard-source-data-status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-right: auto;
    color: #2563eb;
    font-size: 13px;
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-wizard-prev {
    border: 1px solid #cbd5e1 !important;
    color: #475569 !important;
    background: #ffffff !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 8px 16px !important;
    transition: all 0.2s ease !important;

    &:hover {
      background: #f1f5f9 !important;
      color: #1e293b !important;
    }
  }

  .btn-wizard-next {
    font-weight: 600 !important;
    border-radius: 8px !important;
    padding: 8px 20px !important;
    background: #2563eb !important;
    border-color: #2563eb !important;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25) !important;
    transition: all 0.2s ease !important;

    &:disabled {
      opacity: 0.6;
      box-shadow: none !important;
    }

    &:hover:not(:disabled) {
      background: #1d4ed8 !important;
      border-color: #1d4ed8 !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35) !important;
    }
  }

  .btn-wizard-commit {
    font-weight: 700 !important;
    border-radius: 8px !important;
    padding: 8px 20px !important;
    background: #10b981 !important;
    border-color: #10b981 !important;
    box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25) !important;
    transition: all 0.2s ease !important;

    &:hover:not(:disabled) {
      background: #059669 !important;
      border-color: #059669 !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35) !important;
    }
  }

  .btn-wizard-close {
    border: 1.5px solid #cbd5e1 !important;
    color: #475569 !important;
    background: #ffffff !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    padding: 8px 18px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;

    &:hover, &:focus {
      background: #f1f5f9 !important;
      color: #0f172a !important;
      border-color: #94a3b8 !important;
      transform: translateY(-1px);
    }
  }
}
.kpi-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}
.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  text-align: center;
}
.kpi-title {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}
.kpi-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}
.kpi-card--danger {
  border-color: #fca5a5;
  background: #fef2f2;
}
.kpi-card--success {
  border-color: #86efac;
  background: #f0fdf4;
}
.custom-fee-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.custom-fee-header-icon {
  font-size: 26px;
  color: #10b981;
}
.custom-fee-header-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.custom-fee-header-subtitle {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}
.custom-fee-label {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}
.custom-fee-calc-box {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}
.calc-box-left {
  display: flex;
  flex-direction: column;
}
.calc-box-formula {
  font-size: 11px;
  color: #047857;
}
.calc-box-text {
  font-size: 13px;
  font-weight: 700;
  color: #065f46;
  margin-top: 2px;
}
.calc-box-amount {
  font-size: 18px;
  font-weight: 800;
  color: #059669;
}

/* Giới hạn chiều cao Dialog Wizard và cuộn nội dung mượt mà */
/deep/ .tdi-excel-wizard-dialog {
  max-height: 90vh !important;
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;

  .el-dialog__header {
    flex-shrink: 0;
    padding: 16px 24px 14px !important;
    border-bottom: 1px solid #f1f5f9;
  }

  .el-dialog__body {
    flex: 1 1 auto;
    overflow-y: auto !important;
    max-height: calc(90vh - 145px) !important;
    padding: 16px 24px 12px !important;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
      background: #f8fafc;
    }
  }

  .el-dialog__footer {
    flex-shrink: 0;
    padding: 12px 24px 16px !important;
    border-top: 1px solid #f1f5f9;
    background: #ffffff;
  }
}
</style>

<style lang="scss">
/* Global fallback style cho Dialog Wizard để đảm bảo giới hạn chiều cao luôn có hiệu lực */
.tdi-excel-wizard-dialog {
  max-height: 90vh !important;
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;

  .el-dialog__header {
    flex-shrink: 0;
  }

  .el-dialog__body {
    flex: 1 1 auto;
    overflow-y: auto !important;
    max-height: calc(90vh - 145px) !important;
  }

  .el-dialog__footer {
    flex-shrink: 0;
  }
}
</style>
